/* ==========================================================================
   Western Sol — Sauna Designer
   --------------------------------------------------------------------------
   Part 0   Imports
   Part 1   Data (CONFIG, WOODS, HEATERS)
   Part 2   Units and formatting
   Part 3   State
   Part 4   Pure calculations (volume, heaters, fit, computeSpec)
   Part 5   Procedural wood textures
   Part 6   Renderer, scene, lights
   Part 7   Parametric room geometry
   Part 8   Camera and wall visibility
   Part 9   UI binding
   Part 10  Boot

   Units: every dimension is in inches. One Three.js unit = one inch.
   Axes: x runs along the length (back and front walls), z runs along the
   width (front to back), y is up. The floor sits at y = 0 and the room is
   centred on x = 0, z = 0. The front wall is +z, the back wall is -z.
   ========================================================================== */

/* --------------------------------------------------------------------------
   Part 0 — Imports
   Pinned jsDelivr ESM builds. The add-on builds import three from
   /npm/three@0.160.0/+esm, so every module shares one Three.js instance.
   Absolute URLs are used because Shopify injects module scripts in <head>
   before this section renders, and some browsers ignore an import map that
   arrives after a module script.
   -------------------------------------------------------------------------- */
import * as THREE from 'https://cdn.jsdelivr.net/npm/three@0.160.0/+esm';
import { OrbitControls } from 'https://cdn.jsdelivr.net/npm/three@0.160.0/examples/jsm/controls/OrbitControls.js/+esm';
import { RoomEnvironment } from 'https://cdn.jsdelivr.net/npm/three@0.160.0/examples/jsm/environments/RoomEnvironment.js/+esm';
import { GLTFLoader } from 'https://cdn.jsdelivr.net/npm/three@0.160.0/examples/jsm/loaders/GLTFLoader.js/+esm';
import { MeshoptDecoder } from 'https://cdn.jsdelivr.net/npm/three@0.160.0/examples/jsm/libs/meshopt_decoder.module.js/+esm';

/* --------------------------------------------------------------------------
   Part 1 — Data
   Values marked TODO are pending confirmation from the build team.
   -------------------------------------------------------------------------- */
export const CONFIG = {
  limits: { // inches. 3' to 10' on all three, in half-inch steps
    length: { min: 36, max: 120 },
    width: { min: 36, max: 120 },
    height: { min: 36, max: 120 },
  },
  dimStep: 0.5,
  defaultDims: { length: 60, width: 84, height: 84 }, // opening size, 5' × 7' × 7'
  wallThickness: 4,                  // TODO
  floorThickness: 2,                 // slab under the interior floor, render only
  boardExposure: 4.3,                // T&G wall coverage per board, inches
  tgBevel: 0.125,                    // last 1/8" of each board bevels back toward the wall
  wastePct: 10,                      // vapor barrier overlap allowance, percent
  kerf: 0.125,                       // saw cut between pieces taken from one board
  texture: {
    tileLength: 144,                 // inches of board length per tile; longer than any wall run so boards never repeat
    boardsPerTile: 8,                // boards per tile; tile height = boardsPerTile × boardExposure
  },
  bench: {
    depth: 22,                       // all benches
    // Top of ledger for each tier; the seat boards sit on top. Tiers step
    // 17" up and 22" back; the top tier is TODO.
    tops: { low: 17, mid: 34, high: 51 },
    minFloor: 24,                    // open floor kept in front of and beside the benches, TODO
    // 2x6 seat boards run the length of the bench with a 1/8" gap; the last
    // row is ripped to fill the depth.
    slatWidth: 5.5, slatGap: 0.125,
    slatThickness: 1.5,
    // 2x4 cedar frame, top flush at bench height. Every edge gets a ledger.
    // On a wall-backed bench the front ledger is doubled past doubleOver. A
    // bench with no wall behind it, held only at its two ends, gets doubled
    // back and front ledgers past freeDoubleOver.
    ledger: { thickness: 1.5, height: 3.5, doubleOver: 48, freeDoubleOver: 60 },
    joistSpacing: 16,                // on centre, between the end ledgers
    // Cleats under each seat panel, between joists. Ripped from cedar 2x4,
    // two per board. Spacing is TODO.
    cleat: { width: 1.5, maxSpacing: 24 },
    // 2x6 on the outer side of every floating face, flush with the ledger
    // top. Counts inside the bench depth.
    fascia: { thickness: 1.5, height: 5.5 },
    // Full support: horizontal 2x6s under the fascia, butted tight with no
    // gaps and flush with its face. They run down to the bench in front or
    // the floor.
    skirt: { thickness: 1.5, boardWidth: 5.5 },
    minHeadroom: 24,                 // ceiling clearance above the top bench, TODO
    minLength: 24,                   // shortest bench run, TODO
  },
  door: { width: 27.5, height: 82, frameIncluded: true, minFromCorner: 6, clearDepth: 24 }, // minFromCorner, clearDepth (kept free inside the door) TODO
  doorFrame: { face: 1.75, glass: 0.315, bottomGap: 0.5 }, // jamb face width, 8 mm glass, gap under the glass, TODO
  duckboard: { boardWidth: 5.5, gap: 0.375, thickness: 1.5 }, // 2x6; gap is the minimum, boards spread evenly, TODO
  tile: { size: 12, grout: 0.1875 },  // render only
  vaporRoll: { widthIn: 48, lengthFt: 80 }, // one roll covers 320 sq ft
  vaporTape: { lengthFt: 164 }, // foil tape for barrier seams and edges
  benchLed: { color: 0xffa24d },     // hidden strip under each bench's back seam
  heaterWallClearance: 2,            // inches from any wall and the ceiling
  heaterBenchClearance: 2,           // inches from any bench, TODO: confirm per Saunum manual
  vent: { diameter: 4, fromCeiling: 6, fromSideWall: 6, placement: 'wall opposite heater' }, // offsets to vent centre
  glassVolumePenalty: 0,             // cu ft added per sq ft of glass when sizing heater, TODO (0 = off)
  leadEndpoint: null,                // TODO: HubSpot or proxy URL for Save / Send to contractor
  camera: {
    fov: 35,
    startPolar: 56,                  // degrees from straight down
    startAzimuth: 34,                // degrees around y, from the front wall toward the right wall
    maxPolar: 80,                    // keeps the camera above floor level
    tweenMs: 420,
  },
};

// Grain is drawn to read like each species: lines per board, how dark the
// grain runs against the board, how much it waves, knot chance and size
// (inches), and board-to-board colour spread. A wood with a `scan` photo
// uses it for the board faces instead: `pieces` are [x0, x1, y0, y1] pixel
// boxes, each inside one board of the photo and clear of its joints, and
// `stretch` lengthens short close-up pieces along the board, `relief` tilts
// the surface with the grain, and `contrast` below 1 calms figure that gets
// blotchy when a small photo is enlarged.
// `color` is about the average face colour of the product shot; the scan
// is shifted to it. `photo` is that product shot, shown in the picker.
export const WOODS = [
  {
    id: 'thermo-alder', name: 'Thermo-Alder', note: 'Fine, even grain. Light brown.',
    tint: '#c9a36e', texture: null, photo: 'sd-wood-thermo-alder-photo.jpg',
    // Same face as aspen. Only the colour changes: alder is the lightest.
    scan: {
      file: 'sd-wood-thermo-aspen.jpg',
      color: '#c9a36e',
      stretch: 1.25,
      contrast: 0.82,
      relief: 0.16,
      roughness: 0.88,
      env: 0.35,
      pieces: [[0, 1024, 4, 86], [0, 1024, 92, 174], [0, 1024, 180, 262], [0, 1024, 268, 342]],
    },
    grain: { lines: [14, 22], contrast: 0.06, wave: 0.6, knots: 0, knotSize: 0, boardVar: 0.03 },
  },
  {
    id: 'thermo-aspen', name: 'Thermo-Aspen', note: 'Long, flowing grain and clear of knots. Warm brown.',
    tint: '#754325', texture: null, photo: 'sd-wood-thermo-aspen-photo.jpg',
    // One close-up of the face, cut into board-height bands. `stretch` runs
    // that short crop down the length of a real board so the figure does not
    // stamp every few feet. `relief` is how hard the grain tilts the surface.
    scan: {
      file: 'sd-wood-thermo-aspen.jpg',
      color: '#754325',
      stretch: 1.25,
      contrast: 0.82,
      relief: 0.16,
      roughness: 0.88,
      env: 0.35,
      pieces: [[0, 1024, 4, 86], [0, 1024, 92, 174], [0, 1024, 180, 262], [0, 1024, 268, 342]],
    },
    grain: { lines: [14, 22], contrast: 0.06, wave: 0.6, knots: 0, knotSize: 0, boardVar: 0.03 },
  },
  {
    id: 'thermo-pine', name: 'Thermo-Radiata Pine', note: 'The same grain, in a deep brown.',
    tint: '#3d2818', texture: null, photo: 'sd-wood-thermo-pine-photo.jpg',
    // Same face as aspen, shifted dark so it sits under aspen.
    scan: {
      file: 'sd-wood-thermo-aspen.jpg',
      color: '#3d2818',
      stretch: 1.25,
      contrast: 0.82,
      relief: 0.16,
      roughness: 0.88,
      env: 0.35,
      pieces: [[0, 1024, 4, 86], [0, 1024, 92, 174], [0, 1024, 180, 262], [0, 1024, 268, 342]],
    },
    grain: { lines: [14, 22], contrast: 0.06, wave: 0.6, knots: 0, knotSize: 0, boardVar: 0.03 },
  },
];

// Room volume ranges are cubic feet. Dimensions in inches, measured from the
// Saunum CAD models; `model` keys into PAGE_CONFIG.heaterModels.
export const HEATERS = [
  { id: 'saunum-air-7', name: 'Saunum Air 7', kw: 6.9, minCF: 177, maxCF: 318, w: 18.31, d: 12.42, h: 78.41, style: 'tower', model: 'air-7', image: 'heater-saunum-air.jpg' },
  { id: 'saunum-perfect-7', name: 'Saunum Perfect 7', kw: 7.0, minCF: 210, maxCF: 350, w: 19.27, d: 14.08, h: 44.54, style: 'stones', model: 'perfect', image: 'heater-saunum-perfect.jpg' },
  { id: 'saunum-air-l-10', name: 'Saunum Air L 10', kw: 9.9, minCF: 283, maxCF: 499, w: 18.31, d: 12.42, h: 87.21, style: 'tower', model: 'air-l-10', image: 'heater-saunum-air.jpg' },
  { id: 'saunum-perfect-10', name: 'Saunum Perfect 10', kw: 10, minCF: 318, maxCF: 565, w: 19.27, d: 14.08, h: 44.54, style: 'stones', model: 'perfect', image: 'heater-saunum-perfect.jpg' },
];
// All 240V. 7 kW models ≈ 29A, 10 kW models ≈ 41A.

// Board stock from the Western Sol price sheet (BB_Custom_mats_All_Lengths):
// [length in inches, price per board in dollars]. 2x6 is for benches, floor
// and fascia, T&G for walls and ceiling, cedar 2x4 for bench framing.
export const STOCK = {
  'thermo-aspen': {
    name: 'Thermo-Aspen',
    board: [[82.875, 97.93], [94.75, 111.92], [118.25, 139.9]],
    tg: [[23.5, 10.3], [35.5, 15.45], [47.5, 20.6], [59.125, 25.75], [71.25, 30.9], [82.875, 36.05], [94.75, 41.2], [106.5, 46.35], [118.25, 51.5]],
  },
  'thermo-pine': {
    name: 'Thermo-Radiata Pine',
    board: [[23.5, 25.6], [35.5, 38.4], [47.5, 51.2], [59.125, 64], [71.25, 76.8], [82.875, 89.6], [94.75, 102.4], [106.5, 115.2], [118.25, 128]],
    tg: [[23.5, 10.2], [35.5, 15.3], [47.5, 20.4], [59.125, 25.5], [71.25, 30.6], [82.875, 35.7], [94.75, 40.8], [106.5, 45.9], [118.25, 51]],
  },
  'thermo-alder': {
    name: 'Thermo-Alder',
    board: [[106.5, 113.14], [118.25, 125.71]],
    tg: [[23.5, 9.43], [35.5, 14.14], [47.5, 18.86], [59.125, 23.57], [71.25, 28.29], [82.875, 33], [94.75, 37.71], [118.25, 47.14]],
  },
  cedar: { name: 'Cedar', framing: [[96, 35.96]] },
};

const DIMS = ['length', 'width', 'height'];

/* --------------------------------------------------------------------------
   Part 2 — Units and formatting
   -------------------------------------------------------------------------- */
export function formatFtIn(inches) {
  const halves = Math.round(inches * 2);
  const ft = Math.floor(halves / 24);
  const restHalves = halves - ft * 24;
  if (restHalves === 0) return `${ft}'`;
  const whole = Math.floor(restHalves / 2);
  const half = restHalves % 2 === 1 ? '½' : '';
  const rest = `${whole === 0 && half ? '' : whole}${half}"`;
  return ft === 0 ? rest : `${ft}' ${rest}`;
}

export function splitFtIn(inches) {
  const halves = Math.round(inches * 2);
  const ft = Math.floor(halves / 24);
  return { ft, inch: (halves - ft * 24) / 2 };
}

// Reads 7' 6", 7'6, 7 6, 90", 90.5 or 7.5. A bare number of 10 or less is
// feet, since no dimension is under 3'.
export function parseFtIn(text) {
  const t = String(text).trim().toLowerCase()
    .replace(/½/g, '.5').replace(/[’′]/g, "'").replace(/[”″]/g, '"')
    .replace(/\s*(ft|feet|foot)\b/g, "'").replace(/\s*(in|inch|inches)\b/g, '"');
  const n = '(\\d+(?:\\.\\d+)?|\\.\\d+)';
  let m = t.match(new RegExp(`^${n}\\s*'\\s*(?:${n}\\s*"?)?$`));
  if (m) return Number(m[1]) * 12 + Number(m[2] || 0);
  m = t.match(new RegExp(`^${n}\\s*"$`));
  if (m) return Number(m[1]);
  m = t.match(new RegExp(`^${n}\\s+${n}$`));
  if (m) return Number(m[1]) * 12 + Number(m[2]);
  m = t.match(new RegExp(`^${n}$`));
  if (m) return Number(m[1]) <= 10 ? Number(m[1]) * 12 : Number(m[1]);
  return null;
}

function roundToStep(value, step) {
  return Math.round(value / step) * step;
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

/* --------------------------------------------------------------------------
   Part 3 — State
   One object drives everything: UI writes to it, then the scene rebuilds.
   Each step's choice starts empty and the room builds up as they are made:
   wall count, size, wood (walls and ceiling), benches, floor, heater, door.
   -------------------------------------------------------------------------- */
export const WALL_COUNT_OPTIONS = [4, 5, 6];

export const state = {
  wallCount: 4,                      // 4 | 5 | 6 — live is rectangle-only; poly UI is optional
  walls: [],                         // per-wall lengths (inches) when wallCount is 5 or 6
  dims: { ...CONFIG.defaultDims },
  wood: { id: null, orientation: 'horizontal' },
  benches: { layout: null, lSide: 'auto', stepSide: 'back', support: 'floating' }, // the step always sits in front of the back seat
  lighting: { underBench: true },
  floor: null,                       // 'wood' (duckboard) or 'tile'
  heater: { id: null, wall: null, position: 0.5 },
  door: { wall: 'front', placement: null, position: 0.5, type: 'full-glass' },
  owner: '',                         // first name, titles the breakdown
  view: 'outside',
};

// Walls are numbered clockwise starting at the door wall (Wall 1).
export function wallLengthLabel(index) {
  return `Wall ${index + 1}`;
}

function clampWallLength(raw) {
  const { min, max } = CONFIG.limits.length;
  return clamp(roundToStep(raw, CONFIG.dimStep), min, max);
}

export function defaultWallLengths(count, dims = state.dims) {
  const L = dims.length;
  const W = dims.width;
  if (count === 5) return [L, W, L * 0.75, W * 0.75, L * 0.5].map(clampWallLength);
  if (count === 6) return [L, W * 0.55, L * 0.55, W * 0.55, L * 0.55, W].map(clampWallLength);
  return [L, W, L, W].map(clampWallLength);
}

function ensureWallLengths(count) {
  if (count < 5) {
    state.walls = [];
    return;
  }
  const next = defaultWallLengths(count);
  for (let i = 0; i < count; i++) {
    if (Number.isFinite(state.walls[i])) next[i] = clampWallLength(state.walls[i]);
  }
  state.walls = next;
}

// Door centre on the door wall as a 0..1 position in wall-frame order, from
// the placement as seen from outside. Left and right keep minFromCorner.
export function doorPosition(dims, placement, stateIn = null) {
  const len = wallFrame(dims, 'front', stateIn).len;
  const edge = (CONFIG.door.minFromCorner + CONFIG.door.width / 2) / len;
  if (placement === 'left') return clamp(1 - edge, 0.5, 1);
  if (placement === 'right') return clamp(edge, 0, 0.5);
  return 0.5;
}

/* --------------------------------------------------------------------------
   Part 4 — Pure calculations
   No DOM, no Three.js. Safe to unit test and to reuse for pricing.
   -------------------------------------------------------------------------- */
export function roomVolumeCF(dims, glassSqFt = 0, stateIn = null) {
  const floorArea = stateIn && isPolyRoom(stateIn) ? roomPlan(stateIn).area : dims.length * dims.width;
  return (floorArea * dims.height) / 1728 + glassSqFt * CONFIG.glassVolumePenalty;
}

export function heaterOptions(volumeCF) {
  const fits = HEATERS
    .filter((h) => volumeCF >= h.minCF && volumeCF <= h.maxCF)
    .sort((a, b) => a.kw - b.kw || a.maxCF - b.maxCF);
  return { recommended: fits[0] || null, options: fits };
}

export const OPPOSITE_WALL = { front: 'back', back: 'front', left: 'right', right: 'left' };

export function isPolyRoom(stateIn) {
  return Number(stateIn?.wallCount) > 4 && Array.isArray(stateIn.walls) && stateIn.walls.length >= 5;
}

// Cyclic floor plan from side lengths. Exact wall lengths (no close-warp),
// door wall first, then rotate so the door sits on +z.
const _polyPlanCache = new Map();

function cyclicPolyVerts(lengths) {
  const n = lengths.length;
  const Ls = lengths.map((L) => Math.max(Number(L) || 0, 1));
  const maxL = Math.max(...Ls);
  // Circumradius R where Σ 2·asin(Lᵢ/(2R)) = 2π.
  let lo = maxL / 2 + 1e-4;
  let hi = Math.max(lo * 2, Ls.reduce((a, b) => a + b, 0));
  const angleSum = (R) => Ls.reduce((s, L) => {
    const x = Math.min(1, L / (2 * R));
    return s + 2 * Math.asin(x);
  }, 0);
  // If the sides cannot form a cyclic polygon, ease the longest side down
  // until they can (rare; keeps a closable room instead of NaNs).
  const fit = [...Ls];
  for (let guard = 0; guard < 20 && angleSum(lo) <= 2 * Math.PI; guard++) {
    const i = fit.indexOf(Math.max(...fit));
    fit[i] = Math.max(fit[i] * 0.97, maxL * 0.5);
    lo = Math.max(...fit) / 2 + 1e-4;
  }
  for (let k = 0; k < 60; k++) {
    const mid = (lo + hi) / 2;
    if (angleSum(mid) > 2 * Math.PI) lo = mid;
    else hi = mid;
  }
  const R = hi;
  let ang = 0;
  const verts = [];
  for (let i = 0; i < n; i++) {
    verts.push({ x: R * Math.cos(ang), z: R * Math.sin(ang) });
    ang += 2 * Math.asin(Math.min(1, fit[i] / (2 * R)));
  }
  return verts;
}

export function buildPolyPlan(lengths) {
  const key = lengths.map((n) => roundToStep(n, CONFIG.dimStep)).join(',');
  const cached = _polyPlanCache.get(key);
  if (cached) return cached;

  const n = lengths.length;
  const verts = cyclicPolyVerts(lengths);

  const recenter = (list) => {
    let cx = 0;
    let cz = 0;
    for (const v of list) {
      cx += v.x;
      cz += v.z;
    }
    cx /= list.length;
    cz /= list.length;
    for (const v of list) {
      v.x -= cx;
      v.z -= cz;
    }
  };
  recenter(verts);

  const doorAng = Math.atan2(verts[1].z - verts[0].z, verts[1].x - verts[0].x);
  const rot = Math.PI - doorAng;
  const cosR = Math.cos(rot);
  const sinR = Math.sin(rot);
  for (const v of verts) {
    const rx = v.x * cosR - v.z * sinR;
    const rz = v.x * sinR + v.z * cosR;
    v.x = rx;
    v.z = rz;
  }
  recenter(verts);
  if ((verts[0].z + verts[1].z) / 2 < 0) {
    for (const v of verts) {
      v.x *= -1;
      v.z *= -1;
    }
  }

  const frames = [];
  const byId = {};
  for (let i = 0; i < n; i++) {
    const a = verts[i];
    const b = verts[(i + 1) % n];
    const lx = b.x - a.x;
    const lz = b.z - a.z;
    const len = Math.hypot(lx, lz) || 1;
    const ax = lx / len;
    const az = lz / len;
    const nx = -az;
    const nz = ax;
    const id = i === 0 ? 'front' : `w${i}`;
    const frame = {
      id,
      index: i,
      len,
      cx: (a.x + b.x) / 2,
      cz: (a.z + b.z) / 2,
      ax,
      az,
      nx,
      nz,
    };
    frames.push(frame);
    byId[id] = frame;
    byId[`w${i}`] = frame;
  }

  let back = frames[Math.floor(n / 2)];
  let bestD = -1;
  for (const f of frames) {
    if (f.index === 0) continue;
    const d = Math.hypot(f.cx - frames[0].cx, f.cz - frames[0].cz);
    if (d > bestD) {
      bestD = d;
      back = f;
    }
  }
  byId.back = back;
  byId.left = frames[1];
  byId.right = frames[n - 1];

  const xs = verts.map((v) => v.x);
  const zs = verts.map((v) => v.z);
  const plan = {
    verts,
    frames,
    byId,
    n,
    area: Math.abs(verts.reduce((sum, v, i) => {
      const u = verts[(i + 1) % n];
      return sum + v.x * u.z - u.x * v.z;
    }, 0)) / 2,
    bbox: {
      length: Math.max(...xs) - Math.min(...xs),
      width: Math.max(...zs) - Math.min(...zs),
      minX: Math.min(...xs),
      maxX: Math.max(...xs),
      minZ: Math.min(...zs),
      maxZ: Math.max(...zs),
    },
  };
  _polyPlanCache.set(key, plan);
  return plan;
}

function rectFrames(dims) {
  const { length: L, width: W } = dims;
  const frames = [
    { id: 'back', index: 0, len: L, cx: 0, cz: -W / 2, ax: 1, az: 0, nx: 0, nz: 1 },
    { id: 'front', index: 1, len: L, cx: 0, cz: W / 2, ax: -1, az: 0, nx: 0, nz: -1 },
    { id: 'left', index: 2, len: W, cx: -L / 2, cz: 0, ax: 0, az: -1, nx: 1, nz: 0 },
    { id: 'right', index: 3, len: W, cx: L / 2, cz: 0, ax: 0, az: 1, nx: -1, nz: 0 },
  ];
  const byId = Object.fromEntries(frames.map((f) => [f.id, f]));
  return {
    verts: [
      { x: -L / 2, z: -W / 2 },
      { x: L / 2, z: -W / 2 },
      { x: L / 2, z: W / 2 },
      { x: -L / 2, z: W / 2 },
    ],
    frames,
    byId,
    n: 4,
    area: L * W,
    bbox: { length: L, width: W, minX: -L / 2, maxX: L / 2, minZ: -W / 2, maxZ: W / 2 },
  };
}

export function roomPlan(stateIn) {
  if (isPolyRoom(stateIn)) return buildPolyPlan(stateIn.walls);
  return rectFrames(stateIn.dims);
}

export function wallIds(stateIn) {
  return roomPlan(stateIn).frames.map((f) => f.id);
}

export function oppositeWallId(stateIn, wallId = stateIn.door.wall) {
  if (!isPolyRoom(stateIn)) return OPPOSITE_WALL[wallId] || 'back';
  const plan = roomPlan(stateIn);
  const base = plan.byId[wallId] || plan.byId.front;
  let best = plan.frames[0];
  let bestD = -1;
  for (const f of plan.frames) {
    if (f.id === base.id) continue;
    const d = Math.hypot(f.cx - base.cx, f.cz - base.cz);
    if (d > bestD) {
      bestD = d;
      best = f;
    }
  }
  return best.id;
}

// Sync bounding-box length/width from the polygon so camera, lights, and
// volume math keep working. Height stays user-set.
function syncPolyDims(stateIn = state) {
  if (!isPolyRoom(stateIn)) return;
  const { bbox } = roomPlan(stateIn);
  const length = clampWallLength(bbox.length);
  const width = clampWallLength(bbox.width);
  if (stateIn.dims.length === length && stateIn.dims.width === width) return;
  stateIn.dims = { ...stateIn.dims, length, width };
}

// Interior face of each wall in plan. (cx, cz) is the face centre, (ax, az)
// runs along the wall to the right as you face it from inside, (nx, nz)
// points into the room. Pass state as the third argument for 5/6-wall rooms.
export function wallFrame(dims, id, stateIn = null) {
  if (stateIn && isPolyRoom(stateIn)) {
    const plan = roomPlan(stateIn);
    return plan.byId[id] || plan.byId.front;
  }
  const { length: L, width: W } = dims;
  switch (id) {
    case 'back': return { id, len: L, cx: 0, cz: -W / 2, ax: 1, az: 0, nx: 0, nz: 1 };
    case 'front': return { id, len: L, cx: 0, cz: W / 2, ax: -1, az: 0, nx: 0, nz: -1 };
    case 'left': return { id, len: W, cx: -L / 2, cz: 0, ax: 0, az: -1, nx: 1, nz: 0 };
    default: return { id: 'right', len: W, cx: L / 2, cz: 0, ax: 0, az: 1, nx: -1, nz: 0 };
  }
}

function wallFrameOf(stateIn, id) {
  return wallFrame(stateIn.dims, id, stateIn);
}

// a: inches along the wall from its centre, d: inches into the room.
export function frameToPlan(frame, a, d) {
  return { x: frame.cx + frame.ax * a + frame.nx * d, z: frame.cz + frame.az * a + frame.nz * d };
}

// The wall at one end of a frame: side -1 is on the left, +1 on the right.
function wallAtEnd(dims, frame, side, stateIn = null) {
  const ids = stateIn && isPolyRoom(stateIn) ? wallIds(stateIn) : ['back', 'front', 'left', 'right'];
  return ids
    .map((id) => wallFrame(dims, id, stateIn))
    .find((f) => Math.abs(f.nx - (-side * frame.ax)) < 0.01 && Math.abs(f.nz - (-side * frame.az)) < 0.01)
    || ids.map((id) => wallFrame(dims, id, stateIn)).find((f) => f.id !== frame.id && (
      Math.hypot(f.cx - (frame.cx + side * frame.ax * frame.len / 2), f.cz - (frame.cz + side * frame.az * frame.len / 2))
      < Math.hypot(f.cx - (frame.cx - side * frame.ax * frame.len / 2), f.cz - (frame.cz - side * frame.az * frame.len / 2))
    ));
}

function distanceToWall(frame, point) {
  return (point.x - frame.cx) * frame.nx + (point.z - frame.cz) * frame.nz;
}

// 'left' or 'right' as you face the back wall. Auto picks the side wall
// farther from the door; a centred door picks the left.
export function resolveLSide(stateIn) {
  if (stateIn.benches.lSide === 'left' || stateIn.benches.lSide === 'right') return stateIn.benches.lSide;
  const dims = stateIn.dims;
  const doorFrame = wallFrameOf(stateIn, stateIn.door.wall);
  const doorPoint = frameToPlan(doorFrame, (stateIn.door.position - 0.5) * doorFrame.len, 0);
  const back = wallFrameOf(stateIn, oppositeWallId(stateIn));
  const leftEnd = wallAtEnd(dims, back, -1, stateIn);
  const rightEnd = wallAtEnd(dims, back, 1, stateIn);
  if (!leftEnd || !rightEnd) return 'left';
  const toLeft = distanceToWall(leftEnd, doorPoint);
  const toRight = distanceToWall(rightEnd, doorPoint);
  return toRight > toLeft + 0.5 ? 'right' : 'left';
}

// Bench layouts, smallest room first. Each one is offered only when the
// room has space for it (benchNeeds).
export const BENCH_LAYOUTS = [
  { id: 'two-tier', name: 'Two-tier', desc: 'Upper seat at 34" and a 17" step in front, both along the back wall.' },
  { id: 'l-shape', name: 'L-shape', desc: 'One 17" seat along the back wall and one side.' },
  { id: 'l-step', name: 'L with a step', desc: 'A 34" L-shaped seat with a 17" step in front of one side, wall to wall.' },
  { id: 'three-tier', name: 'Three-tier', desc: 'Seats at 51", 34" and 17", stepping down from the back wall.' },
];

// Seat surface for each tier: the ledger top plus the seat boards.
function seatTops() {
  const b = CONFIG.bench;
  return Object.fromEntries(Object.entries(b.tops).map(([k, v]) => [k, v + b.slatThickness]));
}

// Bench runs in wall-local inches: a0..a1 along the wall, d0..d1 out from
// it, top = height of the seat surface. Every tier is `depth` deep and sits
// directly in front of the one above it.
export function benchRuns(stateIn) {
  const dims = stateIn.dims;
  const b = CONFIG.bench;
  const D = b.depth;
  const tops = seatTops();
  const layout = stateIn.benches.layout;
  const back = wallFrameOf(stateIn, oppositeWallId(stateIn));
  const full = { a0: -back.len / 2, a1: back.len / 2 };

  if (layout === 'two-tier' || layout === 'three-tier') {
    const levels = layout === 'two-tier'
      ? [['upper', tops.mid], ['lower', tops.low]]
      : [['top', tops.high], ['upper', tops.mid], ['lower', tops.low]];
    return {
      layout,
      side: null,
      runs: levels.map(([id, top], i) => ({ id, wall: back.id, ...full, d0: i * D, d1: (i + 1) * D, top })),
    };
  }

  const side = resolveLSide(stateIn);
  const sideFrame = wallAtEnd(dims, back, side === 'left' ? -1 : 1, stateIn);
  const half = sideFrame.len / 2;
  const plusEndIsBack = distanceToWall(back, frameToPlan(sideFrame, half, 0)) < distanceToWall(back, frameToPlan(sideFrame, -half, 0));
  // Spans that start `off` inches out from the corner and run to the far wall.
  const sideFrom = (off) => (plusEndIsBack ? { a0: -half, a1: half - off } : { a0: -half + off, a1: half });
  const backFrom = (off) => (side === 'left' ? { a0: full.a0 + off, a1: full.a1 } : { a0: full.a0, a1: full.a1 - off });
  // One L at a single height, `inset` out from both walls. The back cap
  // stops at the corner, covering the end of the side cap.
  const cornerTrim = D - b.fascia.thickness;
  const lPair = (prefix, top, inset) => [
    { id: `${prefix}back`, wall: back.id, ...backFrom(inset), d0: inset, d1: inset + D, top, fascia: backFrom(inset + cornerTrim) },
    { id: `${prefix}side`, wall: sideFrame.id, ...sideFrom(inset + D), d0: inset, d1: inset + D, top },
  ];

  let runs;
  if (layout === 'l-step') {
    // The step runs wall to wall, passing under the end of the upper seat,
    // so it is held at both ends by wall ledgers.
    const step = stateIn.benches.stepSide === 'side'
      ? { id: 'step', wall: sideFrame.id, a0: -half, a1: half, d0: D, d1: 2 * D, top: tops.low }
      : { id: 'step', wall: back.id, ...full, d0: D, d1: 2 * D, top: tops.low };
    runs = [...lPair('upper-', tops.mid, 0), step];
  } else {
    runs = lPair('', tops.low, 0);
  }
  return { layout, side, runs };
}

// Room each layout needs: depth from the back wall to the door wall, back
// wall length, and height. Each keeps minFloor of open floor beside the
// benches and minHeadroom over the top seat.
export function benchNeeds(layout, stepSide = 'back') {
  const { depth: D, minFloor: F, minLength: M, minHeadroom: R } = CONFIG.bench;
  const tops = seatTops();
  switch (layout) {
    case 'two-tier': return { depth: 2 * D + F, back: M, height: tops.mid + R };
    case 'three-tier': return { depth: 3 * D + F, back: M, height: tops.high + R };
    case 'l-step': return stepSide === 'side'
      ? { depth: Math.max(D + F, D + M), back: 2 * D + F, height: tops.mid + R }
      : { depth: 2 * D + F, back: Math.max(D + F, D + M), height: tops.mid + R };
    default: return { depth: D + Math.max(F, M), back: D + F, height: tops.low + R };
  }
}

function roomForBenches(stateIn) {
  const back = wallFrameOf(stateIn, oppositeWallId(stateIn));
  const door = wallFrameOf(stateIn, stateIn.door.wall);
  // Depth is how far the door wall sits from the back wall; back is the
  // seating wall length. Works for rectangles and irregular polygons.
  const depth = Math.abs((door.cx - back.cx) * back.nx + (door.cz - back.cz) * back.nz);
  return { back: back.len, depth, height: stateIn.dims.height };
}

export function benchFits(stateIn, layout, stepSide = 'back') {
  const room = roomForBenches(stateIn);
  const need = benchNeeds(layout, stepSide);
  return room.depth >= need.depth && room.back >= need.back && room.height >= need.height;
}

// Layouts this room has space for; an L with a step counts if either side fits.
export function benchChoices(stateIn) {
  return BENCH_LAYOUTS.filter((l) => benchFits(stateIn, l.id));
}

// Front cap span along the run; full length unless trimmed at a corner.
export function benchFascia(run) {
  return run.fascia || { a0: run.a0, a1: run.a1 };
}

// Seat boards across the full bench depth, over the fascia, slatGap apart.
// The front row is ripped to whatever width is left. rows are { d0, d1 }.
export function benchSlats(run) {
  const { slatWidth: w, slatGap: gap } = CONFIG.bench;
  const rows = [];
  for (let d = run.d0; d < run.d1 - 0.25; d += w + gap) rows.push({ d0: d, d1: Math.min(d + w, run.d1) });
  return { count: rows.length, gap, rows };
}

// Full-support skirt under a run's front cap, in the run's wall-local inches:
// segments { a0, a1, y0, y1, onFloor }. Each segment runs down to the
// highest bench directly in front of it, or to floorY. It spans the cap, so
// at an inside corner it meets the neighbouring skirt the same way the caps do.
export function benchSkirt(run, runs, dims, floorY = 0) {
  const b = CONFIG.bench;
  const frame = wallFrame(dims, run.wall);
  const { a0: s0, a1: s1 } = benchFascia(run);
  const probe = run.d1 + 0.5;
  const alongX = Math.abs(frame.ax) === 1;

  const landings = [];
  for (const other of runs) {
    if (other === run) continue;
    const g = wallFrame(dims, other.wall);
    const p = frameToPlan(g, other.a0, other.d0);
    const q = frameToPlan(g, other.a1, other.d1);
    const [x0, x1] = [Math.min(p.x, q.x), Math.max(p.x, q.x)];
    const [z0, z1] = [Math.min(p.z, q.z), Math.max(p.z, q.z)];
    const at = frameToPlan(frame, 0, probe);
    let lo;
    let hi;
    if (alongX) {
      if (at.z < z0 || at.z > z1) continue;
      [lo, hi] = [(x0 - at.x) / frame.ax, (x1 - at.x) / frame.ax];
    } else {
      if (at.x < x0 || at.x > x1) continue;
      [lo, hi] = [(z0 - at.z) / frame.az, (z1 - at.z) / frame.az];
    }
    if (lo > hi) [lo, hi] = [hi, lo];
    lo = Math.max(lo, s0);
    hi = Math.min(hi, s1);
    if (hi > lo) landings.push({ lo, hi, top: other.top });
  }

  const cuts = [...new Set([s0, s1, ...landings.flatMap((l) => [l.lo, l.hi])])].sort((p, q) => p - q);
  const y1 = run.top - b.slatThickness - b.fascia.height;
  const segments = [];
  for (let i = 0; i < cuts.length - 1; i++) {
    const mid = (cuts[i] + cuts[i + 1]) / 2;
    const tops = landings.filter((l) => l.lo <= mid && mid <= l.hi).map((l) => l.top);
    const y0 = tops.length ? Math.max(...tops) : floorY;
    const last = segments[segments.length - 1];
    if (last && last.y0 === y0) last.a1 = cuts[i + 1];
    else segments.push({ a0: cuts[i], a1: cuts[i + 1], y0, y1, onFloor: !tops.length });
  }
  return segments.filter((s) => s.y1 > s.y0);
}

// Horizontal skirt boards stacked tight up a segment, the full length of it,
// as { y0, y1 } rows. The top row is ripped to fit.
export function skirtBoards(seg) {
  const w = CONFIG.bench.skirt.boardWidth;
  const rows = [];
  for (let y = seg.y0; y < seg.y1 - 0.01; y += w) rows.push({ y0: y, y1: Math.min(y + w, seg.y1) });
  return rows;
}

// The 2x4 frame under a run, in the run's wall-local inches. Every edge gets
// a ledger: on a wall when it meets one (wall = that wall's id), otherwise
// floating (wall = null). Long floating edges are doubled (see
// CONFIG.bench.ledger). The back and front ledgers run the full length
// behind the fascia; the end ledgers fit between them. inner is the open
// frame between.
export function benchFrame(run, dims) {
  const { thickness: t, doubleOver, freeDoubleOver } = CONFIG.bench.ledger;
  const frame = wallFrame(dims, run.wall);
  const half = frame.len / 2;
  const len = run.a1 - run.a0;
  const plies = (wall, span) => (wall || span <= doubleOver ? 1 : 2);
  const backWall = run.d0 === 0 ? run.wall : null;
  const freePlies = len > freeDoubleOver ? 2 : 1;
  const backPlies = backWall ? 1 : freePlies;
  const frontPlies = backWall ? plies(null, len) : freePlies;
  const front = run.d1 - CONFIG.bench.fascia.thickness;
  const d0 = run.d0 + backPlies * t;
  const d1 = front - frontPlies * t;
  const endWall = (end) => (Math.abs(end === -1 ? run.a0 + half : run.a1 - half) < 0.01 ? wallAtEnd(dims, frame, end).id : null);
  const startWall = endWall(-1);
  const finishWall = endWall(1);
  const startPlies = plies(startWall, d1 - d0);
  const finishPlies = plies(finishWall, d1 - d0);
  const inner = { a0: run.a0 + startPlies * t, a1: run.a1 - finishPlies * t, d0, d1 };
  const ledgers = [
    { edge: 'back', wall: backWall, plies: backPlies, a0: run.a0, a1: run.a1, d0: run.d0, d1: d0, lengthIn: len },
    { edge: 'front', wall: null, plies: frontPlies, a0: run.a0, a1: run.a1, d0: d1, d1: front, lengthIn: len },
    { edge: 'start', wall: startWall, plies: startPlies, a0: run.a0, a1: inner.a0, d0, d1, lengthIn: d1 - d0 },
    { edge: 'end', wall: finishWall, plies: finishPlies, a0: inner.a1, a1: run.a1, d0, d1, lengthIn: d1 - d0 },
  ];
  return { ledgers, joists: benchJoists(run, inner), inner };
}

// Joists across the depth between the back and front ledgers, 16" on centre
// from the start of the run. The end ledgers sit at each end of the run, so
// only the joists between them are added; the last bay may come out short.
function benchJoists(run, inner) {
  const t = CONFIG.bench.ledger.thickness;
  const spacing = CONFIG.bench.joistSpacing;
  const joists = [];
  for (let c = run.a0 + t / 2 + spacing; c + t / 2 < inner.a1 - 0.01; c += spacing) {
    joists.push({ a0: c - t / 2, a1: c + t / 2, d0: inner.d0, d1: inner.d1, lengthIn: inner.d1 - inner.d0 });
  }
  return joists;
}

// Seat panels along a run: the longest seat board in stock sets how many
// panels it takes. Each panel gets cleats in its joist bays, no more than
// maxSpacing apart and at least two.
export function benchPanels(run, maxBoardIn) {
  const len = run.a1 - run.a0;
  const count = Math.ceil(len / maxBoardIn - 1e-6);
  const lengthIn = len / count;
  return { count, lengthIn, cleatsPerPanel: Math.max(2, Math.ceil(lengthIn / CONFIG.bench.cleat.maxSpacing)) };
}

// Fit notes: { id, ok, message, link? }. Messages are written so they read
// well as a list under the size inputs.
function doorFitNote(stateIn) {
  const { height: H } = stateIn.dims;
  const door = CONFIG.door;
  const doorWallLength = wallFrameOf(stateIn, stateIn.door.wall || 'front').len;
  const doorWallNeed = door.width + door.minFromCorner * 2;
  if (H < door.height) {
    return { id: 'door', ok: false, message: `The door is ${formatFtIn(door.height)} tall. Raise the height to ${formatFtIn(door.height)} or more to add it.` };
  }
  if (doorWallLength < doorWallNeed) {
    return { id: 'door', ok: false, message: `The door needs a wall at least ${formatFtIn(doorWallNeed)} long, corners included.` };
  }
  return { id: 'door', ok: true, message: '' };
}

// What a layout still needs from this room, as phrases; empty when it fits.
function benchShortfall(stateIn, layout, stepSide) {
  const room = roomForBenches(stateIn);
  const need = benchNeeds(layout, stepSide);
  const out = [];
  if (room.depth < need.depth) out.push(`${formatFtIn(need.depth)} from the back wall to the door wall`);
  if (room.back < need.back) out.push(`a back wall at least ${formatFtIn(need.back)} long`);
  if (room.height < need.height) out.push(`${formatFtIn(need.height)} of height`);
  return out;
}

function joinAnd(items) {
  return items.join(', ').replace(/, ([^,]*)$/, ' and $1');
}

function benchFitNote(stateIn) {
  const { layout, stepSide } = stateIn.benches;
  if (!layout) return { id: 'benches', ok: true, message: '' };
  const needs = benchShortfall(stateIn, layout, stepSide);
  const name = BENCH_LAYOUTS.find((l) => l.id === layout).name;
  return needs.length
    ? { id: 'benches', ok: false, message: `${name} needs ${joinAnd(needs)}.` }
    : { id: 'benches', ok: true, message: '' };
}

function heaterFitNote(stateIn) {
  const volume = roomVolumeCF(stateIn.dims, 0, stateIn);
  const talk = { href: '/pages/contact-us', text: 'Talk to us about this room' };
  const { options } = heaterOptions(volume);
  if (!options.length) {
    const minCF = Math.min(...HEATERS.map((h) => h.minCF));
    const maxCF = Math.max(...HEATERS.map((h) => h.maxCF));
    return { id: 'heater', ok: false, message: `This room is ${Math.round(volume)} cu ft. Our heaters are sized for ${minCF} to ${maxCF} cu ft.`, link: talk };
  }
  if (!heaterChoices(stateIn).length) {
    const shortest = Math.min(...options.map((h) => h.h)) + CONFIG.heaterWallClearance;
    if (stateIn.dims.height < shortest) {
      return { id: 'heater', ok: false, message: `The shortest heater for this room needs ${formatFtIn(shortest)} of height.`, link: talk };
    }
    return { id: 'heater', ok: false, message: 'The benches leave no clear stretch of wall for the heater. The L-shape layout or a larger room opens one up.', link: talk };
  }
  return { id: 'heater', ok: true, message: '' };
}

export function hasBenches(stateIn) {
  return !!stateIn.benches.layout && benchFitNote(stateIn).ok;
}

export function checkFit(stateIn) {
  return [doorFitNote(stateIn), benchFitNote(stateIn), heaterFitNote(stateIn)];
}

/* --------------------------------------------------------------------------
   Part 4b — Heater and vent placement
   The heater stands 2" off its wall and its neighbours, keeps clear of the
   benches and the door entry, and slides along whatever wall span is left.
   -------------------------------------------------------------------------- */
const WALL_IDS = ['back', 'front', 'left', 'right'];

function planRect(dims, wallId, a0, a1, d0, d1, stateIn = null) {
  const frame = wallFrame(dims, wallId, stateIn);
  // All four corners — two-point AABB is wrong for diagonal polygon walls.
  const corners = [
    frameToPlan(frame, a0, d0),
    frameToPlan(frame, a1, d0),
    frameToPlan(frame, a0, d1),
    frameToPlan(frame, a1, d1),
  ];
  return {
    x0: Math.min(...corners.map((c) => c.x)),
    x1: Math.max(...corners.map((c) => c.x)),
    z0: Math.min(...corners.map((c) => c.z)),
    z1: Math.max(...corners.map((c) => c.z)),
  };
}

function rectInFrame(frame, rect) {
  const corners = [[rect.x0, rect.z0], [rect.x1, rect.z0], [rect.x0, rect.z1], [rect.x1, rect.z1]];
  const as = corners.map(([x, z]) => (x - frame.cx) * frame.ax + (z - frame.cz) * frame.az);
  const ds = corners.map(([x, z]) => (x - frame.cx) * frame.nx + (z - frame.cz) * frame.nz);
  return { a0: Math.min(...as), a1: Math.max(...as), d0: Math.min(...ds), d1: Math.max(...ds) };
}

function doorPlanPoint(stateIn) {
  const frame = wallFrameOf(stateIn, stateIn.door.wall);
  return frameToPlan(frame, (stateIn.door.position - 0.5) * frame.len, 0);
}

// Plan rectangles the heater must keep clear of, each with its own gap.
function heaterObstacles(stateIn) {
  const out = [];
  if (hasBenches(stateIn)) {
    for (const run of benchRuns(stateIn).runs) {
      out.push({ rect: planRect(stateIn.dims, run.wall, run.a0, run.a1, run.d0, run.d1, stateIn), gap: CONFIG.heaterBenchClearance });
    }
  }
  if (doorFitNote(stateIn).ok) {
    const frame = wallFrameOf(stateIn, stateIn.door.wall);
    const centre = (stateIn.door.position - 0.5) * frame.len;
    const half = CONFIG.door.width / 2;
    out.push({ rect: planRect(stateIn.dims, stateIn.door.wall, centre - half, centre + half, 0, CONFIG.door.clearDepth, stateIn), gap: 0 });
  }
  return out;
}

// Open spans for the heater's centre along one wall, in wall-local inches.
export function heaterSpans(stateIn, heater, wallId) {
  const frame = wallFrameOf(stateIn, wallId);
  const c = CONFIG.heaterWallClearance;
  const lo = -frame.len / 2 + c + heater.w / 2;
  const hi = frame.len / 2 - c - heater.w / 2;
  if (hi < lo) return [];
  let spans = [[lo, hi]];
  const dFront = c + heater.d;
  for (const { rect, gap } of heaterObstacles(stateIn)) {
    const r = rectInFrame(frame, rect);
    if (r.d0 >= dFront + gap || r.d1 <= c - gap) continue;
    const cut0 = r.a0 - gap - heater.w / 2;
    const cut1 = r.a1 + gap + heater.w / 2;
    spans = spans.flatMap(([s0, s1]) => {
      if (cut1 <= s0 || cut0 >= s1) return [[s0, s1]];
      const keep = [];
      if (cut0 > s0) keep.push([s0, cut0]);
      if (cut1 < s1) keep.push([cut1, s1]);
      return keep;
    });
  }
  return spans;
}

function heaterFitsSomewhere(stateIn, heater) {
  if (stateIn.dims.height < heater.h + CONFIG.heaterWallClearance) return false;
  return wallIds(stateIn).some((id) => heaterSpans(stateIn, heater, id).length > 0);
}

// Heaters sized for the room that also fit it, smallest first.
export function heaterChoices(stateIn) {
  return heaterOptions(roomVolumeCF(stateIn.dims, 0, stateIn)).options.filter((h) => heaterFitsSomewhere(stateIn, h));
}

// Picked heater, wall and centre point, or null when none fits.
export function resolveHeater(stateIn) {
  const choices = heaterChoices(stateIn);
  if (!choices.length) return null;
  const heater = choices.find((h) => h.id === stateIn.heater.id) || choices[0];
  const ids = wallIds(stateIn);
  // Prefer non-door walls first so the heater doesn't land on Wall 1 / the
  // door wall unless the user picked it or nothing else fits.
  const doorWall = stateIn.door.wall || 'front';
  const nonDoor = ids.filter((id) => id !== doorWall);
  const order = [stateIn.heater.wall, ...nonDoor, doorWall];
  const seen = new Set();
  for (const wall of order) {
    if (!wall || seen.has(wall)) continue;
    seen.add(wall);
    const spans = heaterSpans(stateIn, heater, wall);
    if (!spans.length) continue;
    const total = spans.reduce((sum, [s0, s1]) => sum + (s1 - s0), 0);
    let t = clamp(stateIn.heater.position, 0, 1) * total;
    let a = spans[spans.length - 1][1];
    for (const [s0, s1] of spans) {
      if (t <= s1 - s0) { a = s0 + t; break; }
      t -= s1 - s0;
    }
    const frame = wallFrameOf(stateIn, wall);
    // Prefer the frame's canonical id (front/w1/…) over aliases like left/back
    // so heater wall buttons can match aria-pressed on polygon rooms.
    return {
      heater,
      wall: frame.id,
      a,
      d0: CONFIG.heaterWallClearance,
      d1: CONFIG.heaterWallClearance + heater.d,
      frame,
      spans,
    };
  }
  return null;
}

// Vent on the wall opposite the heater, in the top corner farther from the door.
export function ventPlacement(stateIn, heaterWall) {
  const v = CONFIG.vent;
  const frame = wallFrameOf(stateIn, oppositeWallId(stateIn, heaterWall));
  const door = doorPlanPoint(stateIn);
  const ends = [-1, 1].map((side) => {
    const a = side * (frame.len / 2 - v.fromSideWall);
    const p = frameToPlan(frame, a, 0);
    return { side, a, dist: Math.hypot(p.x - door.x, p.z - door.z) };
  });
  const far = ends[0].dist >= ends[1].dist ? ends[0] : ends[1];
  const endWall = wallAtEnd(stateIn.dims, frame, far.side, stateIn);
  return {
    wall: frame.id,
    sideWall: endWall ? endWall.id : frame.id,
    a: far.a,
    y: stateIn.dims.height - v.fromCeiling,
    diameter: v.diameter,
  };
}

// Plain-language wall names. Polygon rooms use Wall 1…N; rectangles stay relative to the door.
export function wallLabel(stateIn, id) {
  if (isPolyRoom(stateIn)) {
    const frame = wallFrameOf(stateIn, id);
    if (frame && Number.isFinite(frame.index)) return wallLengthLabel(frame.index);
  }
  if (id === stateIn.door.wall || id === 'front') return 'Door wall';
  if (id === oppositeWallId(stateIn) || id === 'back') return 'Back wall';
  const back = wallFrameOf(stateIn, oppositeWallId(stateIn));
  const left = wallAtEnd(stateIn.dims, back, -1, stateIn);
  return left && left.id === id ? 'Left wall' : 'Right wall';
}

export function computeSpec(stateIn) {
  const { length: L, width: W, height: H } = stateIn.dims;
  const plan = roomPlan(stateIn);
  const sqFt = (sqIn) => Math.round((sqIn / 144) * 10) / 10;
  const fit = checkFit(stateIn);
  const doorFits = fit.find((n) => n.id === 'door').ok && !!stateIn.door.placement;
  const benchesFit = hasBenches(stateIn);

  const glassSqIn = 0;
  const doorSqIn = doorFits ? CONFIG.door.width * CONFIG.door.height : 0;
  const volumeCF = roomVolumeCF(stateIn.dims, glassSqIn / 144, stateIn);
  const heaters = heaterOptions(volumeCF);

  const wallGrossSqIn = plan.frames.reduce((sum, f) => sum + f.len * H, 0);
  const openingsSqIn = doorSqIn + glassSqIn;
  const wallNetSqIn = wallGrossSqIn - openingsSqIn;
  const ceilingSqIn = plan.area;
  const tgLinearFt = cutPieces(stateIn).tg.reduce((sum, p) => sum + p.lengthIn, 0) / 12;

  return {
    dims: { lengthIn: L, widthIn: W, heightIn: H },
    volumeCF: Math.round(volumeCF * 10) / 10,
    recommendedHeater: heaters.recommended ? heaters.recommended.id : null,
    heaterOptions: heaters.options.map((h) => h.id),
    areas: {
      wallGrossSqFt: sqFt(wallGrossSqIn),
      openingsSqFt: sqFt(openingsSqIn),
      wallNetSqFt: sqFt(wallNetSqIn),
      ceilingSqFt: sqFt(ceilingSqIn),
      floorSqFt: sqFt(plan.area),
    },
    tongueAndGroove: {
      boardExposureIn: CONFIG.boardExposure,
      linearFt: Math.round(tgLinearFt),
    },
    wood: stateIn.wood.id,
    door: doorFits ? {
      widthIn: CONFIG.door.width,
      heightIn: CONFIG.door.height,
      wall: stateIn.door.wall,
      placement: stateIn.door.placement,
      type: stateIn.door.type,
      ...doorEdges(stateIn),
    } : null,
    benches: benchesFit ? benchSpec(stateIn) : null,
    benchLed: benchesFit && stateIn.lighting.underBench
      ? { linearFt: Math.round(benchRuns(stateIn).runs.reduce((sum, r) => sum + r.a1 - r.a0, 0) / 12 * 10) / 10 }
      : null,
    floor: floorSpec(stateIn),
    ...(stateIn.heater.id ? heaterSpec(stateIn) : { heater: null, vent: null }),
    fit: fit.filter((n) => !n.ok).map((n) => n.id),
  };
}

// Line items for the review step. Wood is priced from the boards
// materialPlan buys (STOCK); everything else per unit from the theme editor. part names match the scene tags used
// for highlighting. amount is null for items left out of this design.
export function priceDesign(stateIn, prices) {
  const spec = computeSpec(stateIn);
  const plan = materialPlan(stateIn);
  const waste = 1 + CONFIG.wastePct / 100;
  const stock = STOCK[stateIn.wood.id];
  const woodName = stock ? stock.name : '';
  const cost = (part, kind) => (plan.byPart[part] && plan.byPart[part][kind]) || 0;
  const count = (part, kind) => (plan.counts[part] && plan.counts[part][kind]) || 0;
  const lines = [];
  // unit: { qty, one, many, each?, extra } for the quantity and unit price
  // columns. Wood is cut from stock of mixed lengths, so those lines show a
  // count but no unit price.
  const add = (part, name, detail, amount, unit = null) => lines.push({
    part, name, detail, amount: amount === null ? null : Math.round(amount), unit: amount === null ? null : unit,
  });
  const pieces = (qty, one, many) => (qty > 0 ? { qty, one, many } : null);

  if (!stock) {
    add('walls', 'Walls', 'Pick a wood to price it', null);
    add('ceiling', 'Ceiling', 'Pick a wood to price it', null);
  } else {
    add('walls', 'Walls', `${woodName} 1×4 tongue and groove`, cost('walls', 'tg'),
      pieces(count('walls', 'tg'), 'pc', 'pcs'));
    add('ceiling', 'Ceiling', `${woodName} 1×4 tongue and groove`, cost('ceiling', 'tg'),
      pieces(count('ceiling', 'tg'), 'pc', 'pcs'));
  }

  if (spec.benches && !stock) {
    add('benches', 'Benches', 'Pick a wood to price it', null);
  } else if (spec.benches) {
    const b = spec.benches;
    const layout = (BENCH_LAYOUTS.find((l) => l.id === b.layout) || { name: 'Benches' }).name;
    const support = b.support === 'full' ? 'full support' : 'floating';
    add('benches', 'Benches', `${layout}, ${support}, ${woodName} 2×6 seats and fascia`, cost('benches', 'board'),
      pieces(count('benches', 'board'), 'pc', 'pcs'));
    const cedar = plan.materials.find((m) => m.id === 'framing');
    const [, cedarPrice] = STOCK.cedar.framing[0];
    const cedarBoards = cedar ? cedar.boards.reduce((n, row) => n + row.count, 0) : 0;
    add('benches', 'Bench framing', 'Cedar 2×4 ledgers, joists and cleats, 8\' boards', cedar ? cedar.cost : 0,
      { qty: cedarBoards, one: 'board', many: 'boards', each: cedarPrice });
  } else {
    add('benches', 'Benches', 'No benches in this room', null);
  }

  if (spec.floor && spec.floor.type === 'duckboard') {
    add('floor', 'Floor', stock ? `${woodName} 2×6 duckboard` : 'Pick a wood to price it', stock ? cost('floor', 'board') : null,
      pieces(spec.floor.boards, 'board', 'boards'));
  } else {
    add('floor', 'Floor', 'Your tile floor, supplied by you', null);
  }

  if (spec.heater) {
    const heaterPrice = prices.heaters[spec.heater.id] || 0;
    add('heater', 'Heater', `${spec.heater.name}, ${spec.heater.kw} kW, 240V`, heaterPrice,
      { qty: 1, one: 'unit', many: 'units', each: heaterPrice });
  } else {
    add('heater', 'Heater', 'Pick a heater to price it', null);
  }

  if (spec.benchLed) {
    // One LED zone per bench row (each seat height), priced as a kit.
    const rows = Math.min(3, Math.max(1, new Set(benchRuns(stateIn).runs.map((r) => r.top)).size));
    const kit = prices.led[rows] || 0;
    const zones = rows === 1 ? 'Single zone, 1 bench row' : `${rows} zones, ${rows} bench rows`;
    add('led', 'LED lighting', `${zones}, ${Math.ceil(spec.benchLed.linearFt)} ft of warm strip under the benches`, kit,
      { qty: 1, one: 'kit', many: 'kits', each: kit });
  } else {
    add('led', 'LED lighting', spec.benches ? 'No LED lighting' : 'Comes with benches', null);
  }

  if (spec.door) {
    const where = { left: 'on the left', center: 'centered', right: 'on the right' }[spec.door.placement];
    add('door', 'Door', `Full glass, ${formatFtIn(spec.door.widthIn)} × ${formatFtIn(spec.door.heightIn)} with frame, ${where}`, prices.door,
      { qty: 1, one: 'unit', many: 'units', each: prices.door });
  } else {
    add('door', 'Door', 'Pick a door position to price it', null);
  }

  add('vents', 'Vents', 'Fresh-air intake and exhaust, 4" round', prices.vents,
    { qty: 1, one: 'set', many: 'sets', each: prices.vents });

  add('mill', 'Mill charge', 'Every board precut to your room', prices.mill,
    { qty: 1, one: 'charge', many: 'charges', each: prices.mill });

  add('paraffin', 'Paraffin oil', 'Finish oil for the interior wood', prices.paraffin,
    { qty: 1, one: 'can', many: 'cans', each: prices.paraffin });

  const vaporSqFt = Math.ceil((spec.areas.wallGrossSqFt + spec.areas.ceilingSqFt) * waste);
  const roll = CONFIG.vaporRoll;
  const rolls = Math.max(1, Math.ceil(vaporSqFt / ((roll.widthIn / 12) * roll.lengthFt)));
  add('vapor', 'Vapor barrier', `Foil barrier behind walls and ceiling, ${vaporSqFt} sq ft on ${roll.widthIn}" × ${roll.lengthFt}' rolls`, rolls * prices.vaporRoll,
    { qty: rolls, one: 'roll', many: 'rolls', each: prices.vaporRoll });

  const Lft = spec.dims.lengthIn / 12;
  const Wft = spec.dims.widthIn / 12;
  const Hft = spec.dims.heightIn / 12;
  const foilW = roll.widthIn / 12;
  // Polygon rooms use the real wall-length perimeter; rectangles stay 2(L+W).
  const peri = isPolyRoom(stateIn)
    ? roomPlan(stateIn).frames.reduce((sum, f) => sum + f.len, 0) / 12
    : 2 * (Lft + Wft);
  const wallSeamsFt = Math.ceil(peri / foilW) * Hft;
  const floorCeilFt = 2 * peri;
  const ceilAlongL = Math.max(0, Math.ceil(Wft / foilW) - 1) * Lft;
  const ceilAlongW = Math.max(0, Math.ceil(Lft / foilW) - 1) * Wft;
  const doorFt = spec.door ? 2 * (spec.door.widthIn + spec.door.heightIn) / 12 : 0;
  const tapeFt = Math.ceil(wallSeamsFt + floorCeilFt + Math.min(ceilAlongL, ceilAlongW) + doorFt);
  const tape = CONFIG.vaporTape;
  const tapeRolls = Math.max(1, Math.ceil(tapeFt / tape.lengthFt));
  add('tape', 'Vapor tape', `Foil tape on barrier seams and edges, ${tapeFt} ft on ${tape.lengthFt}' rolls`, tapeRolls * prices.vaporTape,
    { qty: tapeRolls, one: 'roll', many: 'rolls', each: prices.vaporTape });

  const total = lines.reduce((sum, l) => sum + (l.amount || 0), 0);
  return { lines, total, materials: stock ? plan.materials : [] };
}

// Compact, URL-safe copy of every choice, for saved and shared links.
export function encodeDesign(s) {
  const data = {
    v: 2,
    wc: s.wallCount || 4,
    d: [s.dims.length, s.dims.width, s.dims.height],
    wl: s.wallCount > 4 ? s.walls.slice(0, s.wallCount) : undefined,
    w: s.wood.id,
    b: s.benches.layout,
    l: s.benches.lSide,
    t: s.benches.stepSide,
    s: s.benches.support,
    e: s.lighting.underBench ? 1 : 0,
    f: s.floor,
    h: [s.heater.id, s.heater.wall, Math.round(s.heater.position * 1000) / 1000],
    o: s.door.placement,
    n: s.owner || undefined,
  };
  return btoa(unescape(encodeURIComponent(JSON.stringify(data)))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// Returns a partial state, keeping only values the designer knows.
export function decodeDesign(code) {
  try {
    const data = JSON.parse(decodeURIComponent(escape(atob(code.replace(/-/g, '+').replace(/_/g, '/')))));
    if (!data || (data.v !== 1 && data.v !== 2)) return null;
    const dims = {};
    DIMS.forEach((dim, i) => {
      const { min, max } = CONFIG.limits[dim];
      const n = Number(data.d && data.d[i]);
      dims[dim] = Number.isFinite(n) ? clamp(roundToStep(n, CONFIG.dimStep), min, max) : CONFIG.defaultDims[dim];
    });
    const pick = (value, allowed) => (allowed.includes(value) ? value : null);
    const [heaterId, heaterWall, heaterPos] = Array.isArray(data.h) ? data.h : [];
    const wallCount = WALL_COUNT_OPTIONS.includes(Number(data.wc)) ? Number(data.wc) : 4;
    let walls = [];
    if (wallCount > 4) {
      const raw = Array.isArray(data.wl) ? data.wl : defaultWallLengths(wallCount, dims);
      walls = Array.from({ length: wallCount }, (_, i) => {
        const n = Number(raw[i]);
        return Number.isFinite(n) ? clampWallLength(n) : defaultWallLengths(wallCount, dims)[i];
      });
    }
    return {
      wallCount,
      walls,
      dims,
      woodId: pick(data.w, WOODS.map((w) => w.id)),
      layout: pick(data.b, BENCH_LAYOUTS.map((l) => l.id)),
      lSide: pick(data.l, ['auto', 'left', 'right']) || 'auto',
      stepSide: 'back',
      support: pick(data.s, ['floating', 'full']) || 'floating',
      underBench: data.e !== 0,
      floor: pick(data.f, ['wood', 'tile']),
      heaterId: pick(heaterId, HEATERS.map((h) => h.id)),
      // Keep null when unset so resolveHeater can prefer a non-door wall.
      // (Previously this fell back to 'front' and parked heaters on the door wall.)
      heaterWall: typeof heaterWall === 'string' && heaterWall ? heaterWall : null,
      heaterPos: Number.isFinite(Number(heaterPos)) ? clamp(Number(heaterPos), 0, 1) : 0.5,
      door: pick(data.o, ['left', 'center', 'right']),
      owner: typeof data.n === 'string' ? data.n.trim().slice(0, 40) : '',
    };
  } catch (e) {
    return null;
  }
}

// Door frame edges from each corner of the door wall, seen from outside.
function doorEdges(stateIn) {
  const frame = wallFrameOf(stateIn, stateIn.door.wall);
  const a = (stateIn.door.position - 0.5) * frame.len;
  const half = CONFIG.door.width / 2;
  const round = (n) => Math.round(n * 10) / 10;
  return { fromLeftCornerIn: round(frame.len / 2 - a - half), fromRightCornerIn: round(frame.len / 2 + a - half) };
}

// Duckboard boards run the length of the room and spread evenly across its
// width with at least the minimum gap. Offsets are each board's near edge
// from the back wall.
export function duckboardBoards(dims) {
  const { boardWidth: w, gap } = CONFIG.duckboard;
  const across = dims.width;
  const count = Math.max(1, Math.floor((across + gap) / (w + gap)));
  const actualGap = count > 1 ? (across - count * w) / (count - 1) : 0;
  return { count, gap: actualGap, offsets: Array.from({ length: count }, (_, i) => i * (w + actualGap)), lengthIn: dims.length };
}

// Horizontal interior spans of a polygon at plan-z (pairs of x0..x1).
function polySpansAtZ(plan, z) {
  const xs = [];
  const verts = plan.verts;
  for (let i = 0; i < verts.length; i++) {
    const a = verts[i];
    const b = verts[(i + 1) % verts.length];
    const dz = b.z - a.z;
    if (Math.abs(dz) < 1e-9) continue;
    const t = (z - a.z) / dz;
    if (t < 0 || t > 1) continue;
    xs.push(a.x + t * (b.x - a.x));
  }
  xs.sort((p, q) => p - q);
  const spans = [];
  for (let i = 0; i + 1 < xs.length; i += 2) {
    if (xs[i + 1] - xs[i] > 0.5) spans.push([xs[i], xs[i + 1]]);
  }
  return spans;
}

// Clipped duckboard planks for a polygon room — shared by render, cut list, and price.
function polyDuckboardLayout(stateIn) {
  const plan = roomPlan(stateIn);
  const { boardWidth: w, gap } = CONFIG.duckboard;
  const { minZ, maxZ } = plan.bbox;
  const across = Math.max(maxZ - minZ, w);
  const count = Math.max(1, Math.floor((across + gap) / (w + gap)));
  const actualGap = count > 1 ? (across - count * w) / (count - 1) : 0;
  const inset = 0.15;
  const pieces = [];
  for (let i = 0; i < count; i++) {
    const z0 = minZ + i * (w + actualGap);
    const z1 = z0 + w;
    let xLo = -Infinity;
    let xHi = Infinity;
    let hit = false;
    for (let s = 0; s <= 4; s++) {
      const spans = polySpansAtZ(plan, z0 + (z1 - z0) * (s / 4));
      if (!spans.length) { hit = false; break; }
      hit = true;
      xLo = Math.max(xLo, spans[0][0]);
      xHi = Math.min(xHi, spans[spans.length - 1][1]);
    }
    const len = hit ? xHi - xLo - 2 * inset : 0;
    if (len < 1) continue;
    pieces.push({
      lengthIn: len,
      zMid: (z0 + z1) / 2,
      xMid: (xLo + xHi) / 2,
      width: w,
    });
  }
  return { pieces, gap: actualGap, boardWidth: w };
}

function floorSpec(stateIn) {
  if (stateIn.floor === 'tile') return { type: 'tile' };
  if (stateIn.floor !== 'wood') return null;
  if (isPolyRoom(stateIn)) {
    const { pieces, gap, boardWidth } = polyDuckboardLayout(stateIn);
    const inches = pieces.reduce((sum, p) => sum + p.lengthIn, 0);
    return {
      type: 'duckboard',
      boards: pieces.length,
      boardWidthIn: boardWidth,
      gapIn: Math.round(gap * 1000) / 1000,
      linearFt: Math.round(inches / 12 * 10) / 10,
    };
  }
  const boards = duckboardBoards(stateIn.dims);
  return {
    type: 'duckboard',
    boards: boards.count,
    boardWidthIn: CONFIG.duckboard.boardWidth,
    gapIn: Math.round(boards.gap * 1000) / 1000,
    linearFt: Math.round((boards.count * boards.lengthIn) / 12 * 10) / 10,
  };
}

function heaterSpec(stateIn) {
  const placed = resolveHeater(stateIn);
  if (!placed) return { heater: null, vent: null };
  const { heater, wall, a, frame } = placed;
  const round = (n) => Math.round(n * 10) / 10;
  const vent = ventPlacement(stateIn, wall);
  return {
    heater: {
      id: heater.id,
      name: heater.name,
      kw: heater.kw,
      wall,
      centreFromLeftEndIn: round(a + frame.len / 2),
      clearanceIn: {
        wall: placed.d0,
        leftEnd: round(a + frame.len / 2 - heater.w / 2),
        rightEnd: round(frame.len / 2 - a - heater.w / 2),
        ceiling: round(stateIn.dims.height - heater.h),
      },
    },
    vent: {
      wall: vent.wall,
      cornerWith: vent.sideWall,
      diameterIn: vent.diameter,
      centreBelowCeilingIn: CONFIG.vent.fromCeiling,
      centreFromSideWallIn: CONFIG.vent.fromSideWall,
    },
  };
}

function benchSpec(stateIn) {
  const round = (n, places = 1) => Math.round(n * 10 ** places) / 10 ** places;
  const { layout, side, runs } = benchRuns(stateIn);
  const full = stateIn.benches.support === 'full';
  const detail = runs.map((run) => {
    const length = run.a1 - run.a0;
    const slats = benchSlats(run);
    const { ledgers, joists } = benchFrame(run, stateIn.dims);
    const skirtIn = full
      ? benchSkirt(run, runs, stateIn.dims).reduce((sum, s) => sum + skirtBoards(s).length * (s.a1 - s.a0), 0)
      : 0;
    return {
      id: run.id,
      wall: run.wall,
      lengthIn: round(length),
      depthIn: run.d1 - run.d0,
      topIn: run.top,
      fromWallIn: [run.d0, run.d1],
      slats: slats.count,
      slatGapIn: round(slats.gap, 3),
      slatLinearFt: round((slats.count * length) / 12),
      ledgers: ledgers.map((l) => ({ edge: l.edge, wall: l.wall, plies: l.plies, lengthIn: round(l.lengthIn) })),
      ledgerLinearFt: round(ledgers.reduce((sum, l) => sum + l.lengthIn * l.plies, 0) / 12),
      joists: joists.length,
      joistLinearFt: round(joists.reduce((sum, j) => sum + j.lengthIn, 0) / 12),
      fasciaLengthIn: round(benchFascia(run).a1 - benchFascia(run).a0),
      skirtLinearFt: round(skirtIn / 12),
    };
  });
  return {
    layout,
    lSide: side,
    support: full ? 'full' : 'floating',
    runs: detail,
    skirtLinearFt: round(detail.reduce((sum, r) => sum + r.skirtLinearFt, 0)),
    slatLinearFt: round(detail.reduce((sum, r) => sum + r.slatLinearFt, 0)),
    ledgerLinearFt: round(detail.reduce((sum, r) => sum + r.ledgerLinearFt, 0)),
    joistLinearFt: round(detail.reduce((sum, r) => sum + r.joistLinearFt, 0)),
    fasciaLinearFt: round(detail.reduce((sum, r) => sum + r.fasciaLengthIn, 0) / 12),
  };
}

/* Cut list and board buying ---------------------------------------------- */

// Every piece of wood the build needs, grouped by what it is cut from:
// tg (walls and ceiling), board (2x6 for benches, floor and fascia),
// framing (cedar 2x4 ledgers and joists) and cleat (strips ripped from
// cedar 2x4). Pieces longer than the longest board are split into equal
// lengths and butted. ceilingAlong is the dimension the ceiling boards run.
export function cutPieces(stateIn, ceilingAlong = 'length') {
  const { length: L, width: W, height: H } = stateIn.dims;
  const stock = STOCK[stateIn.wood.id] || STOCK['thermo-aspen'];
  const lists = { tg: stock.tg, board: stock.board, framing: STOCK.cedar.framing, cleat: STOCK.cedar.framing };
  const pieces = { tg: [], board: [], framing: [], cleat: [] };
  const add = (kind, part, lengthIn, count = 1) => {
    if (lengthIn < 0.25 || count < 1) return;
    const parts = splitPiece(lengthIn, lists[kind]);
    for (let i = 0; i < count; i++) for (const p of parts) pieces[kind].push({ lengthIn: p, part });
  };

  const e = CONFIG.boardExposure;
  const courses = Math.ceil(H / e - 1e-6);
  const door = computeDoor(stateIn);
  for (const id of wallIds(stateIn)) {
    const len = wallFrameOf(stateIn, id).len;
    for (let i = 0; i < courses; i++) {
      if (door && door.wall === id && i * e < CONFIG.door.height) {
        add('tg', 'walls', door.fromLeftCornerIn);
        add('tg', 'walls', door.fromRightCornerIn);
      } else {
        add('tg', 'walls', len);
      }
    }
  }
  if (isPolyRoom(stateIn)) {
    const plan = roomPlan(stateIn);
    const along = Math.max(...plan.frames.map((f) => f.len));
    add('tg', 'ceiling', along, Math.ceil(plan.area / (along * e) - 1e-6) || 1);
  } else {
    const [along, across] = ceilingAlong === 'length' ? [L, W] : [W, L];
    add('tg', 'ceiling', along, Math.ceil(across / e - 1e-6));
  }

  if (hasBenches(stateIn)) {
    const { runs } = benchRuns(stateIn);
    const longestBoard = Math.max(...stock.board.map(([len]) => len));
    for (const run of runs) {
      const panels = benchPanels(run, longestBoard);
      const { ledgers, joists, inner } = benchFrame(run, stateIn.dims);
      add('board', 'benches', panels.lengthIn, benchSlats(run).count * panels.count);
      const cap = benchFascia(run);
      add('board', 'benches', cap.a1 - cap.a0);
      if (stateIn.benches.support === 'full') {
        for (const seg of benchSkirt(run, runs, stateIn.dims)) add('board', 'benches', seg.a1 - seg.a0, skirtBoards(seg).length);
      }
      for (const l of ledgers) add('framing', 'benches', l.lengthIn, l.plies);
      for (const j of joists) add('framing', 'benches', j.lengthIn);
      add('cleat', 'benches', inner.d1 - inner.d0 - 0.25, panels.count * panels.cleatsPerPanel);
    }
  }

  if (stateIn.floor === 'wood') {
    if (isPolyRoom(stateIn)) {
      for (const plank of polyDuckboardLayout(stateIn).pieces) {
        add('board', 'floor', plank.lengthIn);
      }
    } else {
      add('board', 'floor', L, duckboardBoards(stateIn.dims).count);
    }
  }
  return pieces;
}

// A run longer than the longest board is butted from a full stock board
// plus the rest, which can come from offcuts. Picks the stock board that
// costs least counting the rest at the best price per inch, keeping every
// piece at least minPiece long; falls back to equal lengths.
function splitPiece(lengthIn, stock, minPiece = 12) {
  const longest = Math.max(...stock.map(([len]) => len));
  if (lengthIn <= longest + 1e-6) return [lengthIn];
  const perInch = Math.min(...stock.map(([len, price]) => price / len));
  let best = null;
  for (const [len, price] of stock) {
    const rest = lengthIn - len;
    if (len < minPiece || rest < minPiece || rest > longest) continue;
    const score = price + rest * perInch;
    if (!best || score < best.score - 1e-6) best = { score, parts: [len, rest] };
  }
  if (best) return best.parts;
  const n = Math.ceil(lengthIn / longest - 1e-6);
  return Array.from({ length: n }, () => lengthIn / n);
}

function computeDoor(stateIn) {
  const ok = checkFit(stateIn).find((n) => n.id === 'door').ok && !!stateIn.door.placement;
  return ok ? { wall: stateIn.door.wall, ...doorEdges(stateIn) } : null;
}

// Cheapest set of boards that the pieces can be cut from, kerf between cuts.
// Packs longest pieces first into the board with the least room left, trying
// each stock length as the size to open, then trims every board down to the
// cheapest length that still holds its cuts. Keeps the cheapest result.
export function buyBoards(pieces, stock, kerf = CONFIG.kerf) {
  if (!pieces.length) return { boards: [], cost: 0 };
  const sorted = [...pieces].sort((p, q) => q.lengthIn - p.lengthIn);
  const cheapestFor = (used) => stock
    .filter(([len]) => len >= used - 1e-6)
    .reduce((best, s) => (!best || s[1] < best[1] ? s : best), null);
  let best = null;
  for (const [openIn] of stock) {
    const open = [];
    for (const piece of sorted) {
      let target = null;
      for (const b of open) {
        const room = b.capIn - b.usedIn - kerf;
        if (room >= piece.lengthIn - 1e-6 && (!target || room < target.room)) target = { b, room };
      }
      if (target) {
        target.b.usedIn += kerf + piece.lengthIn;
        target.b.cuts.push(piece);
      } else {
        const capIn = openIn >= piece.lengthIn - 1e-6 ? openIn : cheapestFor(piece.lengthIn)[0];
        open.push({ capIn, usedIn: piece.lengthIn, cuts: [piece] });
      }
    }
    const boards = open.map((b) => {
      const [lengthIn, price] = cheapestFor(b.usedIn);
      return { lengthIn, price, cuts: b.cuts };
    });
    const cost = boards.reduce((sum, b) => sum + b.price, 0);
    if (!best || cost < best.cost - 1e-6) best = { boards, cost };
  }
  return best;
}

// Boards to buy for the whole build and what each part costs. Shared stock
// is split between parts by the length of their pieces. The ceiling runs
// whichever way buys less T&G.
export function materialPlan(stateIn) {
  const stock = STOCK[stateIn.wood.id] || STOCK['thermo-aspen'];
  const byLength = cutPieces(stateIn, 'length');
  const byWidth = cutPieces(stateIn, 'width');
  const tgLength = buyBoards(byLength.tg, stock.tg);
  const tgWidth = buyBoards(byWidth.tg, stock.tg);
  const useWidth = tgWidth.cost < tgLength.cost - 1e-6;
  const pieces = useWidth ? byWidth : byLength;
  const [boardIn, boardPrice] = STOCK.cedar.framing[0];
  const strips = buyBoards(pieces.cleat, [[boardIn, boardPrice / 2]]);
  const cleatBoards = Math.ceil(strips.boards.length / 2);
  const bought = {
    tg: useWidth ? tgWidth : tgLength,
    board: buyBoards(pieces.board, stock.board),
    framing: buyBoards(pieces.framing, STOCK.cedar.framing),
  };

  const byPart = {};
  const counts = {};
  const share = (kind, cost) => {
    const total = pieces[kind].reduce((sum, p) => sum + p.lengthIn, 0);
    for (const p of pieces[kind]) {
      byPart[p.part] = byPart[p.part] || {};
      byPart[p.part][kind] = (byPart[p.part][kind] || 0) + (cost * p.lengthIn) / total;
      counts[p.part] = counts[p.part] || {};
      counts[p.part][kind] = (counts[p.part][kind] || 0) + 1;
    }
  };
  share('tg', bought.tg.cost);
  share('board', bought.board.cost);
  share('framing', bought.framing.cost);
  if (pieces.cleat.length) share('cleat', cleatBoards * boardPrice);

  const tally = (boards, extra = []) => {
    const map = new Map();
    for (const b of [...boards, ...extra]) {
      const row = map.get(b.lengthIn) || { lengthIn: b.lengthIn, count: 0, price: b.price };
      row.count += 1;
      map.set(b.lengthIn, row);
    }
    return [...map.values()].sort((p, q) => p.lengthIn - q.lengthIn);
  };
  const cleatRows = Array.from({ length: cleatBoards }, () => ({ lengthIn: boardIn, price: boardPrice }));
  const materials = [
    { id: 'tg', name: `${stock.name} 1×4 T&G`, boards: tally(bought.tg.boards), cost: bought.tg.cost },
    { id: 'board', name: `${stock.name} 2×6`, boards: tally(bought.board.boards), cost: bought.board.cost },
    { id: 'framing', name: `${STOCK.cedar.name} 2×4`, boards: tally(bought.framing.boards, cleatRows), cost: bought.framing.cost + cleatBoards * boardPrice },
  ].filter((m) => m.boards.length);
  return { materials, byPart, counts, ceilingAlong: useWidth ? 'width' : 'length' };
}

/* --------------------------------------------------------------------------
   Part 5 — Procedural wood textures
   One tile = CONFIG.texture.tileLength inches of board length by
   boardsPerTile boards. UVs are written in real inches (Part 7), so a
   board on screen is always exactly CONFIG.boardExposure tall. Boards are
   drawn as single full-length pieces with no end joints.
   -------------------------------------------------------------------------- */
function seededRandom(seed) {
  let s = seed >>> 0;
  return function next() {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hashString(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) h = Math.imul(h ^ str.charCodeAt(i), 16777619);
  return h >>> 0;
}

// Photos for woods with a scan, keyed by wood id; filled by loadWoodScans()
// before anything draws, so a missing photo just falls back to drawn grain.
const woodScans = new Map();

function assetURL(file) {
  const base = document.querySelector('script[src*="sauna-designer"]')?.src || import.meta.url;
  return new URL(file, base).href;
}

function loadWoodScans(timeoutMs = 5000) {
  const loads = WOODS.filter((w) => w.scan).map((wood) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = assetURL(wood.scan.file);
    return img.decode().then(() => woodScans.set(wood.id, flattenScan(img, wood.scan))).catch(() => {});
  });
  return Promise.race([Promise.all(loads), new Promise((r) => setTimeout(r, timeoutMs))]);
}

// Photos carry their own lighting: one board brighter than the next, and
// slow falloff along each board. Within every piece, divide out a heavily
// smoothed brightness along the board and scale to the photo's overall mean,
// so only the grain is left and pieces sit side by side without patches.
function flattenScan(img, { pieces, contrast = 1, color, bias = [0.89, 0.83, 0.75] }) {
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(img, 0, 0);
  const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const px = data.data;
  const W = canvas.width;
  const lum = (i) => 0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2];

  const rgb = [0, 0, 0];
  const cols = pieces.map(([x0, x1, y0, y1]) => {
    const col = new Float32Array(x1 - x0);
    for (let x = x0; x < x1; x++) {
      let s = 0;
      for (let y = y0; y < y1; y++) {
        const k = (y * W + x) * 4;
        s += lum(k);
        rgb[0] += px[k];
        rgb[1] += px[k + 1];
        rgb[2] += px[k + 2];
      }
      col[x - x0] = s / (y1 - y0);
    }
    return col;
  });
  let total = 0;
  let count = 0;
  for (const col of cols) for (const v of col) { total += v; count++; }
  const mean = total / count;
  const pixels = pieces.reduce((n, [x0, x1, y0, y1]) => n + (x1 - x0) * (y1 - y0), 0);

  // Scene lighting lifts and greys the lit walls; this pulls them back toward
  // the target colour while the shaded walls stay close to it.
  const target = color ? [1, 3, 5].map((i) => parseInt(color.slice(i, i + 2), 16)) : null;
  const tone = bias.map((t, c) => (target ? (t * target[c] * pixels) / rgb[c] : t));
  const radius = 90;
  pieces.forEach(([x0, x1, y0, y1], p) => {
    const col = cols[p];
    const n = col.length;
    const prefix = new Float64Array(n + 1);
    for (let i = 0; i < n; i++) prefix[i + 1] = prefix[i] + col[i];
    for (let x = x0; x < x1; x++) {
      const i = x - x0;
      const a = Math.max(0, i - radius);
      const b = Math.min(n, i + radius + 1);
      const gain = mean / ((prefix[b] - prefix[a]) / (b - a));
      for (let y = y0; y < y1; y++) {
        const k = (y * W + x) * 4;
        const l = lum(k) * gain || 1;
        const g = gain * (mean + (l - mean) * contrast) / l;
        px[k] = Math.min(255, px[k] * g * tone[0]);
        px[k + 1] = Math.min(255, px[k + 1] * g * tone[1]);
        px[k + 2] = Math.min(255, px[k + 2] * g * tone[2]);
      }
    }
  });
  ctx.putImageData(data, 0, 0);
  return canvas;
}

// One board face built from pieces of the photo laid end to end, each one
// scaled to the board's real size, randomly flipped, and feathered into the
// one before so there are no visible joins.
function drawScanBoard(c, img, { pieces, stretch = 1 }, y0, boardPx, size, pxPerInchU, rand) {
  const feather = Math.round(6 * pxPerInchU);
  const tmp = document.createElement('canvas');
  tmp.height = Math.ceil(boardPx);
  const t = tmp.getContext('2d');
  let x = -Math.round(rand() * 20 * pxPerInchU);
  let first = true;
  while (x < size) {
    const [sx0, sx1, sy0, sy1] = pieces[Math.floor(rand() * pieces.length)];
    const srcPerInch = (sy1 - sy0) / CONFIG.boardExposure;
    const w = Math.round(((sx1 - sx0) / srcPerInch) * pxPerInchU * stretch);
    tmp.width = w;
    t.save();
    const flipX = rand() < 0.5;
    const flipY = rand() < 0.5;
    t.translate(flipX ? w : 0, flipY ? tmp.height : 0);
    t.scale(flipX ? -1 : 1, flipY ? -1 : 1);
    t.drawImage(img, sx0, sy0, sx1 - sx0, sy1 - sy0, 0, 0, w, tmp.height);
    t.restore();
    if (!first) {
      // destination-in clears everything outside the fill, so one fill
      // covers the whole piece: faded in over `feather`, solid after.
      const fade = t.createLinearGradient(0, 0, w, 0);
      fade.addColorStop(0, 'rgba(0,0,0,0)');
      fade.addColorStop(Math.min(1, feather / w), 'rgba(0,0,0,1)');
      t.globalCompositeOperation = 'destination-in';
      t.fillStyle = fade;
      t.fillRect(0, 0, w, tmp.height);
      t.globalCompositeOperation = 'source-over';
    }
    c.drawImage(tmp, x, y0, w, boardPx);
    x += w - feather;
    first = false;
  }
}

// Turns the grain in a photo tile into a shallow tangent-space normal so the
// figure catches the light, instead of sitting on the board like a decal.
// The tongue-and-groove bevel is already in the normal (green well below
// 128) and is left alone.
function paintGrainRelief(colorCtx, normalCtx, size, tall, relief) {
  const src = colorCtx.getImageData(0, 0, size, tall).data;
  const normal = normalCtx.getImageData(0, 0, size, tall);
  const dst = normal.data;
  const lumAt = (x, y) => {
    const xx = (x + size) % size;
    const yy = y < 0 ? 0 : y >= tall ? tall - 1 : y;
    const k = (yy * size + xx) * 4;
    return src[k] * 0.299 + src[k + 1] * 0.587 + src[k + 2] * 0.114;
  };
  for (let y = 0; y < tall; y++) {
    for (let x = 0; x < size; x++) {
      const k = (y * size + x) * 4;
      if (dst[k + 1] < 90) continue;
      const dx = lumAt(x + 1, y) - lumAt(x - 1, y);
      const dy = lumAt(x, y + 1) - lumAt(x, y - 1);
      const nx = -dx * relief;
      const ny = -dy * relief;
      const nz = 255;
      const len = Math.hypot(nx, ny, nz);
      dst[k] = 128 + (nx / len) * 127;
      dst[k + 1] = 128 + (ny / len) * 127;
      dst[k + 2] = (nz / len) * 255;
    }
  }
  normalCtx.putImageData(normal, 0, 0);
}

function drawWoodTile(wood, px = isSmallScreen.matches ? 1024 : 2048) {
  const size = px; // along the board
  const tall = px; // across the boards
  const grain = wood.grain;
  const boards = CONFIG.texture.boardsPerTile;
  const boardPx = tall / boards;
  const pxPerInchU = size / CONFIG.texture.tileLength;
  const pxPerInchV = boardPx / CONFIG.boardExposure;
  const rand = seededRandom(hashString(wood.id));

  const color = document.createElement('canvas');
  color.width = size;
  color.height = tall;
  const c = color.getContext('2d');

  // Tangent-space normal map. Flat face is (128, 128, 255); green below
  // 128 tilts the surface down the wall.
  const normal = document.createElement('canvas');
  normal.width = size;
  normal.height = tall;
  const n = normal.getContext('2d');
  n.fillStyle = 'rgb(128, 128, 255)';
  n.fillRect(0, 0, size, tall);
  const bevelPx = Math.max(2, Math.round(CONFIG.tgBevel * pxPerInchV));

  const base = new THREE.Color(wood.tint);
  const hsl = { h: 0, s: 0, l: 0 };
  base.getHSL(hsl);
  const hslCss = (h, s, l, a = 1) => `hsla(${(h * 360).toFixed(1)}, ${(clamp(s, 0, 1) * 100).toFixed(1)}%, ${(clamp(l, 0, 1) * 100).toFixed(1)}%, ${a})`;

  // Canvas colour is multiplied by light and tone mapping, so the base is
  // lifted and desaturated a little to land near the swatch tint on screen.
  hsl.h = (hsl.h + 0.012) % 1;
  hsl.l = clamp(hsl.l + 0.1, 0, 0.85);
  hsl.s = clamp(hsl.s * 0.7, 0, 1);

  const scan = woodScans.get(wood.id);
  for (let i = 0; i < boards; i++) {
    const y0 = i * boardPx;
    if (scan) {
      c.save();
      c.beginPath();
      c.rect(0, y0, size, boardPx);
      c.clip();
      drawScanBoard(c, scan, wood.scan, y0, boardPx, size, pxPerInchU, rand);
      c.restore();
      drawBoardEdge(y0);
      continue;
    }
    const l = hsl.l + (rand() - 0.5) * grain.boardVar * 1.3;
    const s = hsl.s + (rand() - 0.5) * grain.boardVar;
    const h = hsl.h + (rand() - 0.5) * 0.012;

    c.save();
    c.beginPath();
    c.rect(0, y0, size, boardPx);
    c.clip();
    c.fillStyle = hslCss(h, s, l);
    c.fillRect(0, y0, size, boardPx);

    // Slow, smooth colour drift along the board.
    const drift = c.createLinearGradient(0, 0, size, 0);
    const stops = 10;
    for (let k = 0; k <= stops; k++) {
      drift.addColorStop(k / stops, hslCss(h, s, l + (rand() - 0.5) * 0.04, 0.5));
    }
    c.fillStyle = drift;
    c.fillRect(0, y0, size, boardPx);

    // Grain: long, gently waving lines along the board.
    const [minLines, maxLines] = grain.lines;
    const lines = minLines + Math.floor(rand() * (maxLines - minLines));
    const k = grain.contrast / 0.13;
    const pxScale = size / 2048;
    for (let g = 0; g < lines; g++) {
      const gy = y0 + rand() * boardPx;
      const amp = (0.4 + rand() * 1.6) * pxPerInchV * 0.25 * grain.wave;
      const freq = ((1 + rand() * 3) * Math.PI * 2) / size;
      const phase = rand() * Math.PI * 2;
      const dark = rand() < 0.7;
      c.strokeStyle = dark
        ? hslCss(h, s + 0.04, l - grain.contrast * 1.1, (0.06 + rand() * 0.1) * Math.min(k, 1.6))
        : hslCss(h, s, l + 0.1, 0.06 + rand() * 0.08);
      c.lineWidth = (0.6 + rand() * 1.6) * (1 + Math.max(0, k - 1) * 0.6) * Math.max(pxScale, 0.5);
      c.beginPath();
      for (let x = 0; x <= size; x += 16) {
        const y = gy + Math.sin(x * freq + phase) * amp;
        if (x === 0) c.moveTo(x, y);
        else c.lineTo(x, y);
      }
      c.stroke();
    }

    // Occasional small knot.
    if (rand() < grain.knots) {
      const kx = rand() * size;
      const ky = y0 + boardPx * (0.3 + rand() * 0.4);
      const kr = grain.knotSize * (0.8 + rand() * 0.6);
      const grad = c.createRadialGradient(kx, ky, 0, kx, ky, kr * pxPerInchV);
      grad.addColorStop(0, hslCss(h, s + 0.05, l - 0.28, 0.85));
      grad.addColorStop(0.55, hslCss(h, s, l - 0.16, 0.45));
      grad.addColorStop(1, hslCss(h, s, l, 0));
      c.save();
      c.translate(kx, ky);
      c.scale((kr * pxPerInchU * 1.6) / (kr * pxPerInchV), 1);
      c.translate(-kx, -ky);
      c.fillStyle = grad;
      c.beginPath();
      c.arc(kx, ky, kr * pxPerInchV, 0, Math.PI * 2);
      c.fill();
      c.restore();
    }

    c.restore();
    drawBoardEdge(y0);
  }

  if (scan && wood.scan.relief) paintGrainRelief(c, n, size, tall, wood.scan.relief);

  return { color, normal };

  // T&G profile: the last 1/8" at the lower edge bevels back toward the
  // wall at about 45°, ending in a hairline joint with the board below.
  // Canvas rows run top to bottom, so the lower edge is the end of the board.
  function drawBoardEdge(y0) {
    const bevelTop = y0 + boardPx - bevelPx;
    n.fillStyle = 'rgb(128, 38, 218)';
    n.fillRect(0, bevelTop, size, bevelPx);
    c.fillStyle = 'rgba(30, 15, 5, 0.16)';
    c.fillRect(0, bevelTop, size, bevelPx);
    c.fillStyle = 'rgba(18, 9, 3, 0.75)';
    c.fillRect(0, y0 + boardPx - 1, size, 1);
  }
}

const woodMaterialCache = new Map();

function woodMaterial(woodId, maxAnisotropy) {
  if (woodMaterialCache.has(woodId)) return woodMaterialCache.get(woodId);
  const wood = WOODS.find((w) => w.id === woodId) || WOODS[0];
  const { color, normal } = drawWoodTile(wood);

  const map = new THREE.CanvasTexture(color);
  map.colorSpace = THREE.SRGBColorSpace;
  const normalMap = new THREE.CanvasTexture(normal);
  for (const tex of [map, normalMap]) {
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    tex.anisotropy = Math.min(8, maxAnisotropy);
  }

  const material = new THREE.MeshStandardMaterial({
    map,
    normalMap,
    roughness: wood.scan?.roughness ?? 0.74,
    metalness: 0,
    envMapIntensity: wood.scan?.env ?? 0.8,
  });
  woodMaterialCache.set(woodId, material);
  return material;
}

// Small swatch of the same procedural tile, for the wood picker.
const swatchCache = new Map();

function woodSwatchURL(wood) {
  if (swatchCache.has(wood.id)) return swatchCache.get(wood.id);
  const px = 512;
  const { color } = drawWoodTile(wood, px);
  const out = document.createElement('canvas');
  out.width = 128;
  out.height = 96;
  const inchU = px / CONFIG.texture.tileLength;
  const boardPx = px / CONFIG.texture.boardsPerTile;
  const boardsShown = 3;
  const lengthShown = (boardsShown * CONFIG.boardExposure * out.width) / out.height;
  out.getContext('2d').drawImage(color, 40 * inchU, 0, lengthShown * inchU, boardsShown * boardPx, 0, 0, out.width, out.height);
  const url = out.toDataURL('image/jpeg', 0.85);
  swatchCache.set(wood.id, url);
  return url;
}

// Stone tile with grout lines; one texture tile is 2 × 2 floor tiles.
function tileMaterial(maxAnisotropy) {
  const px = 512;
  const canvas = document.createElement('canvas');
  canvas.width = px;
  canvas.height = px;
  const c = canvas.getContext('2d');
  const { size, grout } = CONFIG.tile;
  const pxPerIn = px / (size * 2);
  const groutPx = Math.max(2, grout * pxPerIn);
  c.fillStyle = '#9d9891';
  c.fillRect(0, 0, px, px);
  const rand = seededRandom(7);
  for (let i = 0; i < 2; i++) {
    for (let j = 0; j < 2; j++) {
      const x = i * size * pxPerIn + groutPx / 2;
      const y = j * size * pxPerIn + groutPx / 2;
      const w = size * pxPerIn - groutPx;
      const l = 74 + rand() * 6;
      c.fillStyle = `hsl(32, 6%, ${l}%)`;
      c.fillRect(x, y, w, w);
      for (let k = 0; k < 260; k++) {
        c.fillStyle = `hsla(30, 8%, ${l + (rand() - 0.5) * 14}%, 0.18)`;
        c.fillRect(x + rand() * w, y + rand() * w, 1 + rand() * 3, 1 + rand() * 3);
      }
    }
  }
  const map = new THREE.CanvasTexture(canvas);
  map.colorSpace = THREE.SRGBColorSpace;
  map.wrapS = THREE.RepeatWrapping;
  map.wrapT = THREE.RepeatWrapping;
  map.anisotropy = Math.min(8, maxAnisotropy);
  return new THREE.MeshStandardMaterial({ map, roughness: 0.55, metalness: 0, envMapIntensity: 0.6 });
}

// Soft falloff for LED wash decals: brightest along v = 1, fading to 0 at
// v = 0 and toward both ends along u.
function glowTexture() {
  const w = 256;
  const h = 128;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const c = canvas.getContext('2d');
  const img = c.createImageData(w, h);
  for (let y = 0; y < h; y++) {
    const v = 1 - y / (h - 1);
    const fall = Math.pow(v, 2.2);
    for (let x = 0; x < w; x++) {
      const u = x / (w - 1);
      const ends = Math.min(1, Math.min(u, 1 - u) / 0.08);
      const a = fall * ends * ends;
      const i = (y * w + x) * 4;
      img.data[i] = 255 * a;
      img.data[i + 1] = 255 * a;
      img.data[i + 2] = 255 * a;
      img.data[i + 3] = 255;
    }
  }
  c.putImageData(img, 0, 0);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/* --------------------------------------------------------------------------
   Part 6 — Renderer, scene, lights
   -------------------------------------------------------------------------- */
const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const isSmallScreen = window.matchMedia('(max-width: 1023px)');

const dom = {
  section: document.getElementById('sauna-designer'),
  viewport: document.getElementById('sd-viewport'),
  canvas: document.getElementById('sd-canvas'),
  hint: document.getElementById('sd-hint'),
  tilt: document.getElementById('sd-tilt'),
  loading: document.getElementById('sd-loading'),
  fallback: document.getElementById('sd-fallback'),
  fitNotes: document.getElementById('sd-fit-notes'),
  steps: Array.from(document.querySelectorAll('.sd-step')),
  back: document.getElementById('sd-back'),
  next: document.getElementById('sd-next'),
  stepCount: document.getElementById('sd-step-count'),
  wallCounts: document.getElementById('sd-wall-counts'),
  sizeSub: document.getElementById('sd-size-sub'),
  rectDims: document.getElementById('sd-rect-dims'),
  polyDims: document.getElementById('sd-poly-dims'),
  wallLengths: document.getElementById('sd-wall-lengths'),
  polyHeight: document.getElementById('sd-poly-height'),
  polyHeightInput: document.getElementById('sd-poly-height-value'),
  sliders: Array.from(document.querySelectorAll('.sd-slider input[data-dim]')),
  dimInputs: Array.from(document.querySelectorAll('[data-dim-input]')),
  woods: document.getElementById('sd-woods'),
  floors: Array.from(document.querySelectorAll('[data-floor]')),
  doorPlacements: Array.from(document.querySelectorAll('[data-door-placement]')),
  doorNote: document.getElementById('sd-door-note'),
  total: document.getElementById('sd-total'),
  shareToggle: document.getElementById('sd-share-toggle'),
  share: document.getElementById('sd-share'),
  bdSaveToggle: document.getElementById('sd-bd-save-toggle'),
  bdSave: document.getElementById('sd-bd-save'),
  breakdownOpen: document.getElementById('sd-breakdown-open'),
  exportOpen: document.getElementById('sd-export-open'),
  exportDialog: document.getElementById('sd-export'),
  exportForm: document.getElementById('sd-export-form'),
  exportClose: document.getElementById('sd-export-close'),
  exportChips: document.getElementById('sd-export-chips'),
  exportEmail: document.getElementById('sd-export-email'),
  breakdownClose: document.getElementById('sd-breakdown-close'),
  breakdown: document.getElementById('sd-breakdown'),
  owner: document.getElementById('sd-owner'),
  bdTitle: document.getElementById('sd-bd-title'),
  bdSummary: document.getElementById('sd-bd-summary'),
  bdFigure: document.getElementById('sd-bd-figure'),
  bdShot: document.getElementById('sd-bd-shot'),
  bdLines: document.getElementById('sd-bd-lines'),
  bdTotal: document.getElementById('sd-bd-total'),
  bdCalendar: document.getElementById('sd-bd-calendar'),
  benchLayouts: document.getElementById('sd-bench-layouts'),
  benchToggles: document.getElementById('sd-bench-toggles'),
  supports: Array.from(document.querySelectorAll('[data-support]')),
  leds: Array.from(document.querySelectorAll('[data-led]')),
  lSideField: document.getElementById('sd-lside'),
  lSides: Array.from(document.querySelectorAll('[data-lside]')),
  benchNote: document.getElementById('sd-bench-note'),
  heaters: document.getElementById('sd-heaters'),
  heaterNote: document.getElementById('sd-heater-note'),
  heaterPlace: document.getElementById('sd-heater-place'),
  heaterWalls: Array.from(document.querySelectorAll('[data-heater-wall]')),
  heaterPosition: document.getElementById('sd-heater-position'),
};

let renderer;
let scene;
let camera;
let controls;
let keyLight;
let glowLight;
let roomGroup = null;
let walls = {};
let ceiling = null;
let needsRender = true;

const materials = {};

function webglAvailable() {
  try {
    const test = document.createElement('canvas');
    return !!(window.WebGLRenderingContext && (test.getContext('webgl2') || test.getContext('webgl')));
  } catch (e) {
    return false;
  }
}

function initScene() {
  renderer = new THREE.WebGLRenderer({ canvas: dom.canvas, antialias: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, isSmallScreen.matches ? 1.5 : 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.15;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.setClearColor(0xf5f5f7, 1);

  scene = new THREE.Scene();

  const pmrem = new THREE.PMREMGenerator(renderer);
  const envScene = new RoomEnvironment(renderer);
  scene.environment = pmrem.fromScene(envScene, 0.04).texture;
  envScene.traverse((o) => {
    if (o.geometry) o.geometry.dispose();
    if (o.material) o.material.dispose();
  });
  pmrem.dispose();

  camera = new THREE.PerspectiveCamera(CONFIG.camera.fov, 1, 1, 20000);

  // Warm key light from above, slightly in front and to the left.
  keyLight = new THREE.DirectionalLight(0xffecd6, 2.1);
  keyLight.castShadow = true;
  const shadowSize = isSmallScreen.matches ? 1024 : 2048;
  keyLight.shadow.mapSize.set(shadowSize, shadowSize);
  keyLight.shadow.bias = -0.0004;
  keyLight.shadow.normalBias = 0.6;
  scene.add(keyLight);
  scene.add(keyLight.target);

  // Soft warm glow from the ceiling centre, like a sauna at temperature.
  glowLight = new THREE.PointLight(0xffb070, 0.55, 0, 0);
  scene.add(glowLight);

  materials.floor = new THREE.MeshStandardMaterial({ color: 0xd8d4cd, roughness: 0.9, metalness: 0, envMapIntensity: 0.4 });
  materials.ground = new THREE.ShadowMaterial({ opacity: 0.07 });
  materials.ventFace = new THREE.MeshStandardMaterial({ color: 0x1d1d1d, roughness: 0.7, metalness: 0.2 });
  materials.ventRim = new THREE.MeshStandardMaterial({ color: 0xb8b3aa, roughness: 0.4, metalness: 0.7 });
  materials.shellFill = new THREE.MeshStandardMaterial({
    color: 0xc4925f, roughness: 0.85, metalness: 0, transparent: true, opacity: 0.16,
    depthWrite: false, side: THREE.DoubleSide,
  });
  materials.shellEdge = new THREE.LineBasicMaterial({ color: 0x7a5534, transparent: true, opacity: 0.85 });
  materials.subfloor = new THREE.MeshStandardMaterial({ color: 0x2b2622, roughness: 0.95, metalness: 0 });
  materials.tile = tileMaterial(renderer.capabilities.getMaxAnisotropy());
  materials.glass = new THREE.MeshStandardMaterial({
    color: 0x8a7860, roughness: 0.04, metalness: 0.1, transparent: true, opacity: 0.42,
    envMapIntensity: 1.4, depthWrite: false, side: THREE.DoubleSide,
  });
  materials.hardware = new THREE.MeshStandardMaterial({ color: 0xc9c6c0, roughness: 0.25, metalness: 0.9 });
  materials.ledStrip = new THREE.MeshBasicMaterial({ color: CONFIG.benchLed.color });
  materials.ledGlow = new THREE.MeshBasicMaterial({
    map: glowTexture(), color: CONFIG.benchLed.color, transparent: true, opacity: 0.75,
    blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false,
    polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2, side: THREE.DoubleSide,
  });
  materials.ledGlowWall = materials.ledGlow.clone();
  materials.ledGlowWall.opacity = 0.55;
  controls = new OrbitControls(camera, dom.canvas);
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;
  controls.enablePan = false;
  controls.rotateSpeed = 0.75;
  controls.zoomSpeed = 0.8;
  controls.minPolarAngle = THREE.MathUtils.degToRad(8);
  controls.maxPolarAngle = THREE.MathUtils.degToRad(CONFIG.camera.maxPolar);
  controls.addEventListener('change', () => { needsRender = true; syncTilt(); });
  controls.addEventListener('start', cancelCameraTween);
  bindTilt();
  // On touch screens the hint gives the room back once they've tried it.
  if (window.matchMedia('(hover: none) and (pointer: coarse)').matches) {
    const retireHint = () => {
      dom.hint?.classList.add('is-used');
      controls.removeEventListener('start', retireHint);
    };
    controls.addEventListener('start', retireHint);
  }

  const resize = () => {
    const { clientWidth: w, clientHeight: h } = dom.viewport;
    if (!w || !h) return;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    needsRender = true;
  };
  new ResizeObserver(resize).observe(dom.viewport);
  resize();
}

/* --------------------------------------------------------------------------
   Part 7 — Parametric room geometry
   Walls are built as boxes around any openings (Phase 2 adds door and
   glass openings). UVs are written in real inches so T&G seams stay true
   to size no matter how big the wall is.
   -------------------------------------------------------------------------- */

// Write UVs from world position. Vertical faces carry boards along the
// wall (horizontal) or up the wall (vertical). Horizontal faces carry
// boards along x. The room is centred on the origin, so shifting by half a
// tile keeps every run inside one tile and each board stays one piece.
function applyBoardUVs(geometry, offset, orientation) {
  const pos = geometry.attributes.position;
  const nrm = geometry.attributes.normal;
  const uv = geometry.attributes.uv;
  const tileU = CONFIG.texture.tileLength;
  const tileV = CONFIG.boardExposure * CONFIG.texture.boardsPerTile;
  const shift = tileU / 2;

  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i) + offset.x;
    const y = pos.getY(i) + offset.y;
    const z = pos.getZ(i) + offset.z;
    const nx = Math.abs(nrm.getX(i));
    const ny = Math.abs(nrm.getY(i));
    let u;
    let v;
    if (ny > 0.5) {
      u = (x + shift) / tileU;
      v = z / tileV;
    } else {
      const along = nx > 0.5 ? z : x;
      if (orientation === 'vertical') {
        u = y / tileU;
        v = along / tileV;
      } else {
        u = (along + shift) / tileU;
        v = y / tileV;
      }
    }
    uv.setXY(i, u, v);
  }
  uv.needsUpdate = true;
}

// Split a wall into rectangles around openings. Openings are
// { u0, u1, v0, v1 } in wall-local inches: u along the wall, v up from
// the floor. Openings must not overlap along u.
export function panelsAround(length, height, openings = []) {
  const rects = [];
  const sorted = [...openings].sort((a, b) => a.u0 - b.u0);
  let u = 0;
  for (const o of sorted) {
    if (o.u0 > u) rects.push({ u0: u, u1: o.u0, v0: 0, v1: height });
    if (o.v0 > 0) rects.push({ u0: o.u0, u1: o.u1, v0: 0, v1: o.v0 });
    if (o.v1 < height) rects.push({ u0: o.u0, u1: o.u1, v0: o.v1, v1: height });
    u = Math.max(u, o.u1);
  }
  if (u < length) rects.push({ u0: u, u1: length, v0: 0, v1: height });
  return rects.filter((r) => r.u1 - r.u0 > 0.01 && r.v1 - r.v0 > 0.01);
}

// Front and back walls run the full outer length and cover the corners.
// Left and right walls sit between them.
function wallLayout(dims) {
  const { length: L, width: W } = dims;
  const t = CONFIG.wallThickness;
  return [
    { id: 'back', axis: 'x', start: -L / 2 - t, length: L + 2 * t, plane: -W / 2 - t / 2 },
    { id: 'front', axis: 'x', start: -L / 2 - t, length: L + 2 * t, plane: W / 2 + t / 2 },
    { id: 'left', axis: 'z', start: -W / 2, length: W, plane: -L / 2 - t / 2 },
    { id: 'right', axis: 'z', start: -W / 2, length: W, plane: L / 2 + t / 2 },
  ];
}

function boxMesh(width, height, depth, center, material, orientation) {
  const geometry = new THREE.BoxGeometry(width, height, depth);
  applyBoardUVs(geometry, center, orientation);
  const mesh = new THREE.Mesh(geometry, material);
  mesh.position.copy(center);
  return mesh;
}

// Bench pieces are single boards: grain runs along the longest side, and
// v stays inside one board of the tile so no T&G seam lands on a slat.
function applyPieceUVs(geometry, center, index) {
  const { width, height, depth } = geometry.parameters;
  const ext = { x: width, y: height, z: depth };
  const [longAxis, wideAxis, thinAxis] = ['x', 'y', 'z'].sort((p, q) => ext[q] - ext[p]);
  const exposure = CONFIG.boardExposure;
  const tileU = CONFIG.texture.tileLength;
  const tileV = exposure * CONFIG.texture.boardsPerTile;
  const boardMid = (index % CONFIG.texture.boardsPerTile) * exposure + exposure / 2;
  const spread = exposure * 0.34;
  const pos = geometry.attributes.position;
  const uv = geometry.attributes.uv;
  const get = { x: (i) => pos.getX(i), y: (i) => pos.getY(i), z: (i) => pos.getZ(i) };
  for (let i = 0; i < pos.count; i++) {
    const along = get[longAxis](i) + center[longAxis];
    const n1 = get[wideAxis](i) / (ext[wideAxis] / 2);
    const n2 = get[thinAxis](i) / (ext[thinAxis] / 2);
    uv.setXY(i, (along + tileU / 2) / tileU, (boardMid + spread * (0.8 * n1 + 0.2 * n2)) / tileV);
  }
  uv.needsUpdate = true;
}

// One bench run: 2x6 seat boards on a 2x4 frame of ledgers and joists, the
// fascia on the front, plus skirt boards when it has full support.
function buildBenchRun(run, stateIn, wood, seed, skirt) {
  const dims = stateIn.dims;
  const b = CONFIG.bench;
  const frame = wallFrameOf(stateIn, run.wall);
  const group = new THREE.Group();
  group.name = `bench-${run.id}`;
  const slatTop = run.top;
  const slatBottom = slatTop - b.slatThickness;
  const ledgerBottom = slatBottom - b.ledger.height;
  const fasciaBottom = slatBottom - b.fascia.height;
  let index = seed;

  const piece = (a0, a1, d0, d1, y0, y1) => {
    const along = a1 - a0;
    const out = d1 - d0;
    const p = frameToPlan(frame, (a0 + a1) / 2, (d0 + d1) / 2);
    const center = new THREE.Vector3(p.x, (y0 + y1) / 2, p.z);
    const alongX = Math.abs(frame.ax) === 1;
    const geometry = new THREE.BoxGeometry(alongX ? along : out, y1 - y0, alongX ? out : along);
    applyPieceUVs(geometry, center, index++);
    const mesh = new THREE.Mesh(geometry, wood);
    mesh.position.copy(center);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    group.add(mesh);
  };

  for (const row of benchSlats(run).rows) {
    piece(run.a0, run.a1, row.d0, row.d1, slatBottom, slatTop);
  }
  const { ledgers, joists } = benchFrame(run, dims);
  for (const l of [...ledgers, ...joists]) {
    piece(l.a0, l.a1, l.d0, l.d1, ledgerBottom, slatBottom);
  }
  const cap = benchFascia(run);
  piece(cap.a0, cap.a1, run.d1 - b.fascia.thickness, run.d1, fasciaBottom, slatBottom);
  for (const seg of skirt || []) {
    for (const board of skirtBoards(seg)) {
      piece(seg.a0, seg.a1, run.d1 - b.skirt.thickness, run.d1, board.y0, board.y1);
    }
  }
  return group;
}

// Hidden LED strip and the warm wash it throws. Every run carries it on the
// underside of the slats along the back seam, just in front of the back
// ledger, washing down the wall behind (when the run sits on a wall) and out
// across the floor beneath.
function buildBenchLed(run, stateIn, wallGroups, floorTop) {
  const dims = stateIn.dims;
  const b = CONFIG.bench;
  const frame = wallFrameOf(stateIn, run.wall);
  const slatBottom = run.top - b.slatThickness;
  const { inner } = benchFrame(run, dims);
  const ledD = inner.d0 + 0.35;
  const group = new THREE.Group();
  group.name = `bench-led-${run.id}`;
  group.userData.part = 'led';

  const alongX = Math.abs(frame.ax) === 1;
  const p = frameToPlan(frame, (inner.a0 + inner.a1) / 2, ledD);
  const len = inner.a1 - inner.a0 - 0.5;
  const strip = new THREE.Mesh(new THREE.BoxGeometry(alongX ? len : 0.5, 0.25, alongX ? 0.5 : len), materials.ledStrip);
  strip.position.set(p.x, slatBottom - 0.15, p.z);
  group.add(strip);

  // Quad from four (a, d, y) corners; uv v = 1 along the bright edge.
  const quad = (corners) => {
    const pts = corners.map(([a, d, y]) => {
      const q = frameToPlan(frame, a, d);
      return [q.x, y, q.z];
    });
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(pts.flat(), 3));
    geometry.setAttribute('uv', new THREE.Float32BufferAttribute([0, 0, 1, 0, 1, 1, 0, 1], 2));
    geometry.setIndex([0, 1, 2, 0, 2, 3]);
    const mesh = new THREE.Mesh(geometry, materials.ledGlow);
    mesh.renderOrder = 4;
    return mesh;
  };

  const fy = floorTop + 0.05;
  const reach = Math.min(16, run.d1 - run.d0 + 6);
  group.add(quad([[run.a0, run.d0 + reach, fy], [run.a1, run.d0 + reach, fy], [run.a1, run.d0, fy], [run.a0, run.d0, fy]]));

  if (run.d0 === 0 && wallGroups[run.wall]) {
    const top = slatBottom - b.ledger.height;
    const wash = quad([[run.a0, 0.06, floorTop], [run.a1, 0.06, floorTop], [run.a1, 0.06, top], [run.a0, 0.06, top]]);
    wash.material = materials.ledGlowWall;
    wash.userData.part = 'led';
    wallGroups[run.wall].add(wash);
  }
  return group;
}

function buildBenches(s, wood, wallGroups, floorTop) {
  const group = new THREE.Group();
  group.name = 'benches';
  const { runs } = benchRuns(s);
  runs.forEach((run, i) => {
    const skirt = s.benches.support === 'full' ? benchSkirt(run, runs, s.dims, floorTop) : null;
    group.add(buildBenchRun(run, s, wood, i * 3, skirt));
    if (s.lighting.underBench) group.add(buildBenchLed(run, s, wallGroups, floorTop));
  });
  return group;
}

// Saunum CAD models, exported in inches with the back against the wall, the
// front facing +z, the base at y = 0 and centred on x and z. Each loads on
// first use. Nothing is drawn until that file is ready.
const heaterModels = new Map();
let gltfLoader = null;

function heaterModel(key) {
  const url = PAGE_CONFIG.heaterModels?.[key];
  if (!url) return null;
  const entry = heaterModels.get(key);
  if (entry && !entry.retry) return entry.scene || null;
  const tries = (entry?.tries || 0) + 1;
  heaterModels.set(key, { tries });
  gltfLoader ||= new GLTFLoader().setMeshoptDecoder(MeshoptDecoder);
  gltfLoader.load(url, (gltf) => {
    gltf.scene.traverse((obj) => {
      if (!obj.isMesh) return;
      obj.castShadow = true;
      obj.receiveShadow = true;
      obj.userData.shared = true;
    });
    heaterModels.set(key, { scene: gltf.scene });
    scheduleRebuild();
  }, undefined, (err) => {
    console.warn('[sauna-designer] heater model failed', key, err);
    heaterModels.set(key, { tries });
    if (tries < 3) {
      setTimeout(() => {
        heaterModels.set(key, { tries, retry: true });
        scheduleRebuild();
      }, 2000 * tries);
    }
  });
  return null;
}

// The heater at its real size, turned to face into the room. Only the CAD
// model is drawn. The load started above rebuilds the room when it arrives.
function buildHeater(placed) {
  const { heater, frame, a, d0 } = placed;
  const group = new THREE.Group();
  group.name = 'heater';
  const centre = frameToPlan(frame, a, d0 + heater.d / 2);
  group.position.set(centre.x, 0, centre.z);
  group.rotation.y = Math.atan2(frame.nx, frame.nz);

  const model = heaterModel(heater.model);
  if (model) group.add(model.clone());
  return group;
}

// Vent sits on the interior face of its wall, so it hides with the wall.
function buildVent(s, vent) {
  const frame = wallFrameOf(s, vent.wall);
  const r = vent.diameter / 2;
  const group = new THREE.Group();
  group.name = 'vent';
  const face = new THREE.Mesh(new THREE.CylinderGeometry(r, r, 0.3, 40), materials.ventFace);
  face.rotation.x = Math.PI / 2;
  group.add(face);
  const rim = new THREE.Mesh(new THREE.TorusGeometry(r + 0.25, 0.35, 10, 40), materials.ventRim);
  rim.position.z = 0.2;
  group.add(rim);
  const p = frameToPlan(frame, vent.a, 0.2);
  group.position.set(p.x, vent.y, p.z);
  group.rotation.y = Math.atan2(frame.nx, frame.nz);
  return group;
}

function orientAlongWall(geometry, frame) {
  // Local +x along the wall, +y up, +z outward (opposite the inward normal).
  geometry.applyMatrix4(new THREE.Matrix4().makeBasis(
    new THREE.Vector3(frame.ax, 0, frame.az),
    new THREE.Vector3(0, 1, 0),
    new THREE.Vector3(-frame.nx, 0, -frame.nz),
  ));
}

function wallPanelMesh(frame, u0, u1, v0, v1, thickness, material, orientation) {
  const w = u1 - u0;
  const h = v1 - v0;
  const a = (u0 + u1) / 2 - frame.len / 2;
  const y = (v0 + v1) / 2;
  const p = frameToPlan(frame, a, -thickness / 2);
  const center = new THREE.Vector3(p.x, y, p.z);
  const geometry = new THREE.BoxGeometry(w, h, thickness);
  orientAlongWall(geometry, frame);
  applyBoardUVs(geometry, center, orientation);
  const mesh = new THREE.Mesh(geometry, material);
  mesh.position.copy(center);
  return mesh;
}

function buildPlanCeiling(plan, height, thickness, material, orientation) {
  const shape = polyShape(plan);
  const top = new THREE.ShapeGeometry(shape);
  top.rotateX(-Math.PI / 2);
  const mesh = new THREE.Mesh(top, material);
  mesh.position.y = height + thickness / 2;
  applyBoardUVs(top, new THREE.Vector3(0, height + thickness / 2, 0), orientation);
  return mesh;
}

function buildRoom(s) {
  syncPolyDims(s);
  const group = new THREE.Group();
  group.name = 'sauna';
  const { length: L, width: W, height: H } = s.dims;
  const t = CONFIG.wallThickness;
  const wood = woodMaterial(s.wood.id, renderer.capabilities.getMaxAnisotropy());
  const orientation = s.wood.orientation;
  const nextWalls = {};
  const door = hasDoor(s) ? doorOpening(s) : null;
  const floorTop = s.floor === 'wood' ? CONFIG.duckboard.thickness : 0;
  const plan = roomPlan(s);
  const poly = isPolyRoom(s);

  if (poly) {
    for (const frame of plan.frames) {
      const wallGroup = new THREE.Group();
      wallGroup.name = `wall-${frame.id}`;
      const openings = door && frame.id === s.door.wall
        ? [{ u0: door.a0 + frame.len / 2, u1: door.a1 + frame.len / 2, v0: 0, v1: door.height }]
        : [];
      for (const r of panelsAround(frame.len, H, openings)) {
        const mesh = wallPanelMesh(frame, r.u0, r.u1, r.v0, r.v1, t, wood, orientation);
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        mesh.userData.part = 'walls';
        wallGroup.add(mesh);
      }
      nextWalls[frame.id] = wallGroup;
      group.add(wallGroup);
    }
  } else {
    for (const wall of wallLayout(s.dims)) {
      const wallGroup = new THREE.Group();
      wallGroup.name = `wall-${wall.id}`;
      const openings = door && wall.id === s.door.wall
        ? [{ u0: door.x0 - wall.start, u1: door.x1 - wall.start, v0: 0, v1: door.height }]
        : [];
      for (const r of panelsAround(wall.length, H, openings)) {
        const w = r.u1 - r.u0;
        const h = r.v1 - r.v0;
        const mid = wall.start + (r.u0 + r.u1) / 2;
        const y = (r.v0 + r.v1) / 2;
        const mesh = wall.axis === 'x'
          ? boxMesh(w, h, t, new THREE.Vector3(mid, y, wall.plane), wood, orientation)
          : boxMesh(t, h, w, new THREE.Vector3(wall.plane, y, mid), wood, orientation);
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        mesh.userData.part = 'walls';
        wallGroup.add(mesh);
      }
      nextWalls[wall.id] = wallGroup;
      group.add(wallGroup);
    }
  }

  const tagged = (object, part) => {
    object.userData.part = part;
    return object;
  };
  if (door) nextWalls[s.door.wall].add(tagged(buildDoor(s, door, wood), 'door'));
  if (hasBenches(s)) group.add(tagged(buildBenches(s, wood, nextWalls, floorTop), 'benches'));
  if (s.floor === 'wood') group.add(tagged(buildDuckboard(s, wood), 'floor'));

  const placed = s.heater.id ? resolveHeater(s) : null;
  if (placed) {
    group.add(tagged(buildHeater(placed), 'heater'));
    const vent = ventPlacement(s, placed.wall);
    if (nextWalls[vent.wall]) nextWalls[vent.wall].add(tagged(buildVent(s, vent), 'vents'));
  }

  let ceilingMesh;
  if (poly) {
    ceilingMesh = buildPlanCeiling(plan, H, t, wood, orientation);
  } else {
    ceilingMesh = boxMesh(L + 2 * t, t, W + 2 * t, new THREE.Vector3(0, H + t / 2, 0), wood, orientation);
  }
  ceilingMesh.name = 'ceiling';
  ceilingMesh.userData.part = 'ceiling';
  ceilingMesh.receiveShadow = true;
  group.add(ceilingMesh);

  addFloorAndGround(group, s, s.floor);
  return { group, walls: nextWalls, ceiling: ceilingMesh };
}

function hasDoor(s) {
  return !!s.door.placement && doorFitNote(s).ok;
}

// Door opening on the door wall in wall-local inches (a along the wall).
function doorOpening(s) {
  const frame = wallFrameOf(s, s.door.wall);
  const a = (s.door.position - 0.5) * frame.len;
  const half = CONFIG.door.width / 2;
  const centrePlan = frameToPlan(frame, a, 0);
  return {
    frame,
    a,
    a0: a - half,
    a1: a + half,
    centre: centrePlan.x,
    x0: centrePlan.x - half,
    x1: centrePlan.x + half,
    height: Math.min(CONFIG.door.height, s.dims.height),
  };
}

// Full-glass door: wood jambs and head through the wall, a bronze-tinted
// glass leaf, two hinges on the corner side and a wood pull on both faces.
function buildDoor(s, opening, wood) {
  const { face, glass: glassT, bottomGap } = CONFIG.doorFrame;
  const t = CONFIG.wallThickness;
  const frame = opening.frame || wallFrameOf(s, s.door.wall);
  const group = new THREE.Group();
  group.name = 'door';
  const width = opening.a1 - opening.a0;
  const height = opening.height;
  let index = 3;
  const place = (localA, y, out) => {
    const p = frameToPlan(frame, localA, out);
    return new THREE.Vector3(p.x, y, p.z);
  };
  const wooden = (w, h, d, localA, y, out) => {
    const geometry = new THREE.BoxGeometry(w, h, d);
    orientAlongWall(geometry, frame);
    const center = place(localA, y, out);
    applyPieceUVs(geometry, center, index++);
    const mesh = new THREE.Mesh(geometry, wood);
    mesh.position.copy(center);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    group.add(mesh);
  };
  const out = -t / 2;
  wooden(face, height, t + 0.5, opening.a0 + face / 2, height / 2, out);
  wooden(face, height, t + 0.5, opening.a1 - face / 2, height / 2, out);
  wooden(width - 2 * face, face, t + 0.5, opening.a, height - face / 2, out);

  const leafW = width - 2 * face - 0.25;
  const leafH = height - face - bottomGap - 0.125;
  const leafGeo = new THREE.BoxGeometry(leafW, leafH, glassT);
  orientAlongWall(leafGeo, frame);
  const leaf = new THREE.Mesh(leafGeo, materials.glass);
  leaf.position.copy(place(opening.a, bottomGap + leafH / 2, out));
  leaf.renderOrder = 3;
  group.add(leaf);

  const hingeSide = s.door.placement === 'right' ? 1 : -1;
  const hingeA = opening.a + hingeSide * (leafW / 2 - 0.6);
  for (const y of [bottomGap + 9, bottomGap + leafH - 11]) {
    const hingeGeo = new THREE.BoxGeometry(1.2, 3.75, glassT + 0.7);
    orientAlongWall(hingeGeo, frame);
    const hinge = new THREE.Mesh(hingeGeo, materials.hardware);
    hinge.position.copy(place(hingeA, y, out));
    group.add(hinge);
  }

  const pullA = opening.a - hingeSide * (leafW / 2 - 3.5);
  const pullLen = 20;
  const pullY = bottomGap + leafH * 0.5;
  for (const side of [-1, 1]) {
    const standoff = glassT / 2 + 1.6;
    wooden(1.25, pullLen, 1.25, pullA, pullY, out + side * (standoff + 0.6));
    for (const dy of [-pullLen / 2 + 2.5, pullLen / 2 - 2.5]) {
      const post = new THREE.Mesh(new THREE.CylinderGeometry(0.28, 0.28, standoff, 12), materials.hardware);
      post.rotation.x = Math.PI / 2;
      const p = place(pullA, pullY + dy, out + side * standoff / 2);
      post.position.copy(p);
      // Align post through the wall normal.
      post.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), new THREE.Vector3(frame.nx * side, 0, frame.nz * side).normalize());
      group.add(post);
    }
  }
  return group;
}

// Duckboard: boards on the floor running the length of the room, with an
// even gap between each that shows the dark subfloor. Polygon rooms clip
// each board to the floor plan so planks don't stick outside the walls.
function buildDuckboard(s, wood) {
  const { thickness } = CONFIG.duckboard;
  const group = new THREE.Group();
  group.name = 'duckboard';

  if (isPolyRoom(s)) {
    polyDuckboardLayout(s).pieces.forEach((plank, i) => {
      const geometry = new THREE.BoxGeometry(plank.lengthIn, thickness, plank.width);
      const center = new THREE.Vector3(plank.xMid, thickness / 2, plank.zMid);
      applyPieceUVs(geometry, center, i + 5);
      const mesh = new THREE.Mesh(geometry, wood);
      mesh.position.copy(center);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      group.add(mesh);
    });
    return group;
  }

  const { boardWidth: w } = CONFIG.duckboard;
  const { length: L, width: W } = s.dims;
  duckboardBoards(s.dims).offsets.forEach((off, i) => {
    const geometry = new THREE.BoxGeometry(L, thickness, w);
    const center = new THREE.Vector3(0, thickness / 2, -W / 2 + off + w / 2);
    applyPieceUVs(geometry, center, i + 5);
    const mesh = new THREE.Mesh(geometry, wood);
    mesh.position.copy(center);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    group.add(mesh);
  });
  return group;
}

// The room as an empty volume: a see-through box with drawn edges, sized to
// the interior. Shown while the size is being chosen.
function buildShell(s) {
  syncPolyDims(s);
  const group = new THREE.Group();
  group.name = 'sauna-shell';
  const { length: L, width: W, height: H } = s.dims;

  if (isPolyRoom(s)) {
    const plan = roomPlan(s);
    const t = CONFIG.wallThickness;
    // Panel shell with a unique color per wall so Wall 1…N are easy to tell apart.
    for (const frame of plan.frames) {
      const active = focusedWallIndex === frame.index;
      const mat = wallShellMaterial(frame.index, { active });
      const mesh = wallPanelMesh(frame, 0, frame.len, 0, H, t, mat, 'horizontal');
      mesh.renderOrder = active ? 2 : 1;
      mesh.castShadow = false;
      mesh.receiveShadow = false;
      mesh.name = `shell-wall-${frame.index}`;
      group.add(mesh);
      const edgeGeo = new THREE.EdgesGeometry(mesh.geometry);
      const edge = new THREE.LineSegments(edgeGeo, materials.shellEdge);
      edge.position.copy(mesh.position);
      edge.renderOrder = 3;
      group.add(edge);
    }
    const floorShape = polyShape(plan);
    const floorGeo = new THREE.ShapeGeometry(floorShape);
    floorGeo.rotateX(-Math.PI / 2);
    const floorFill = new THREE.Mesh(floorGeo, materials.shellFill);
    floorFill.position.y = 0.05;
    floorFill.renderOrder = 1;
    group.add(floorFill);
  } else {
    const box = new THREE.BoxGeometry(L, H, W);
    const volume = new THREE.Mesh(box, materials.shellFill);
    volume.position.y = H / 2;
    volume.renderOrder = 1;
    const edges = new THREE.LineSegments(new THREE.EdgesGeometry(box), materials.shellEdge);
    edges.position.y = H / 2;
    edges.renderOrder = 2;
    group.add(volume, edges);
  }

  addFloorAndGround(group, s, null);
  return { group, walls: {}, ceiling: null };
}

function polyShape(plan) {
  const shape = new THREE.Shape();
  plan.verts.forEach((v, i) => {
    if (i === 0) shape.moveTo(v.x, -v.z);
    else shape.lineTo(v.x, -v.z);
  });
  shape.closePath();
  return shape;
}

function scalePolyFloorUVs(geometry) {
  // ShapeGeometry UVs are plan inches. The tile atlas is 2×2 tiles
  // (size*2 inches) across the 0–1 UV square, so convert inches → that space.
  const uv = geometry.attributes.uv;
  if (!uv) return;
  const span = CONFIG.tile.size * 2;
  for (let i = 0; i < uv.count; i++) {
    uv.setXY(i, uv.getX(i) / span, uv.getY(i) / span);
  }
  uv.needsUpdate = true;
}

function addFloorAndGround(group, stateOrDims, floorType) {
  const s = stateOrDims.dims ? stateOrDims : { dims: stateOrDims, wallCount: 4, walls: [] };
  const { length: L, width: W } = s.dims;
  const t = CONFIG.wallThickness;
  const ft = CONFIG.floorThickness;
  let floor;
  let floorMaterial = materials.floor;
  if (floorType === 'wood') floorMaterial = materials.subfloor;
  const poly = isPolyRoom(s);
  if (floorType === 'tile') {
    floorMaterial = materials.tile;
    if (poly) {
      // Poly UVs already in tile-atlas units after scalePolyFloorUVs.
      floorMaterial.map.repeat.set(1, 1);
      floorMaterial.map.offset.set(0, 0);
    } else {
      const span = CONFIG.tile.size * 2;
      floorMaterial.map.repeat.set((L + 2 * t) / span, (W + 2 * t) / span);
      floorMaterial.map.offset.set(-t / span, -t / span);
    }
  }

  if (poly) {
    const plan = roomPlan(s);
    const floorGeo = new THREE.ShapeGeometry(polyShape(plan));
    floorGeo.rotateX(-Math.PI / 2);
    if (floorType === 'tile') scalePolyFloorUVs(floorGeo);
    floor = new THREE.Mesh(floorGeo, floorMaterial);
    floor.position.y = -ft / 2;
  } else {
    const floorGeo = new THREE.BoxGeometry(L + 2 * t, ft, W + 2 * t);
    floor = new THREE.Mesh(floorGeo, floorMaterial);
    floor.position.set(0, -ft / 2, 0);
  }
  floor.receiveShadow = true;
  floor.castShadow = true;
  floor.name = 'floor';
  group.add(floor);

  const span = Math.max(L, W) * 6;
  const ground = new THREE.Mesh(new THREE.PlaneGeometry(span, span), materials.ground);
  ground.rotation.x = -Math.PI / 2;
  ground.position.y = -ft - 0.05;
  ground.receiveShadow = true;
  ground.name = 'ground';
  group.add(ground);
}

function disposeGroup(group) {
  group.traverse((obj) => {
    if (obj.userData.shared) return;
    if (obj.geometry) obj.geometry.dispose();
    if (obj.isInstancedMesh) obj.dispose();
  });
}

function placeLights(dims) {
  const { width: W, height: H } = dims;
  const r = roomRadius(dims);
  keyLight.position.set(-r * 0.4, H + r * 3.2, r * 0.6);
  keyLight.target.position.set(0, 0, 0);
  const cam = keyLight.shadow.camera;
  cam.left = -r * 1.3;
  cam.right = r * 1.3;
  cam.top = r * 1.3;
  cam.bottom = -r * 1.3;
  cam.near = 1;
  cam.far = r * 8;
  cam.updateProjectionMatrix();
  glowLight.position.set(0, H - 8, -W * 0.15);
}

/* --------------------------------------------------------------------------
   Part 8 — Camera and wall visibility
   Outside view: orbit like a dollhouse. Any wall the camera is outside of
   is hidden, and the ceiling hides whenever the camera is above it.
   -------------------------------------------------------------------------- */
function roomRadius(dims) {
  const t = CONFIG.wallThickness;
  return 0.5 * Math.hypot(dims.length + 2 * t, dims.width + 2 * t, dims.height + t);
}

function fitDistance(dims) {
  const r = roomRadius(dims);
  const vFov = THREE.MathUtils.degToRad(camera.fov);
  const hFov = 2 * Math.atan(Math.tan(vFov / 2) * camera.aspect);
  const fov = Math.min(vFov, hFov);
  return (r / Math.sin(fov / 2)) * 1.32;
}

function cameraTarget(dims) {
  return new THREE.Vector3(0, dims.height * 0.42, 0);
}

let tween = null;

function cancelCameraTween() {
  tween = null;
  orbit = null;
}

// Thumb at the top looks down from above. Thumb at the bottom sits near eye level.
const tiltOffset = new THREE.Vector3();
const tiltSpherical = new THREE.Spherical();

function currentPolar() {
  tiltOffset.copy(camera.position).sub(controls.target);
  tiltSpherical.setFromVector3(tiltOffset);
  return tiltSpherical.phi;
}

function syncTilt() {
  if (!dom.tilt || !camera || !controls || document.activeElement === dom.tilt) return;
  const min = controls.minPolarAngle;
  const max = controls.maxPolarAngle;
  const t = (max - currentPolar()) / (max - min);
  const value = String(Math.round(clamp(t, 0, 1) * 100));
  if (dom.tilt.value !== value) dom.tilt.value = value;
}

function applyTilt(t) {
  if (!camera || !controls) return;
  cancelCameraTween();
  const min = controls.minPolarAngle;
  const max = controls.maxPolarAngle;
  tiltOffset.copy(camera.position).sub(controls.target);
  tiltSpherical.setFromVector3(tiltOffset);
  tiltSpherical.phi = max - clamp(t, 0, 1) * (max - min);
  tiltOffset.setFromSpherical(tiltSpherical);
  camera.position.copy(controls.target).add(tiltOffset);
  controls.update();
  needsRender = true;
}

function bindTilt() {
  if (!dom.tilt) return;
  dom.tilt.addEventListener('input', () => applyTilt(Number(dom.tilt.value) / 100));
  dom.tilt.addEventListener('change', () => syncTilt());
}

function frameRoom(dims, { instant = false } = {}) {
  const r = roomRadius(dims);
  controls.minDistance = r * 1.12;
  controls.maxDistance = r * 4.5;

  const target = cameraTarget(dims);
  const distance = fitDistance(dims);

  if (!camera.userData.placed) {
    const offset = new THREE.Vector3().setFromSphericalCoords(
      distance,
      THREE.MathUtils.degToRad(CONFIG.camera.startPolar),
      THREE.MathUtils.degToRad(CONFIG.camera.startAzimuth),
    );
    controls.target.copy(target);
    camera.position.copy(target).add(offset);
    camera.userData.placed = true;
    controls.update();
    needsRender = true;
    syncTilt();
    return;
  }

  const fromTarget = controls.target.clone();
  const fromDistance = camera.position.distanceTo(controls.target);
  if (instant || prefersReducedMotion.matches) {
    applyCameraFrame(target, distance);
    return;
  }
  tween = { start: performance.now(), fromTarget, fromDistance, toTarget: target, toDistance: distance };
}

function applyCameraFrame(target, distance) {
  const dir = camera.position.clone().sub(controls.target).normalize();
  controls.target.copy(target);
  camera.position.copy(target).addScaledVector(dir, distance);
  needsRender = true;
}

// Swing the camera around the room to a spherical angle, keeping its distance.
let orbit = null;

function orbitTo(azimuthDeg, polarDeg) {
  if (!camera) return;
  const offset = camera.position.clone().sub(controls.target);
  const from = new THREE.Spherical().setFromVector3(offset);
  const toTheta = THREE.MathUtils.degToRad(azimuthDeg);
  let dTheta = ((toTheta - from.theta) % (Math.PI * 2) + Math.PI * 3) % (Math.PI * 2) - Math.PI;
  const to = { theta: from.theta + dTheta, phi: THREE.MathUtils.degToRad(polarDeg) };
  if (prefersReducedMotion.matches) {
    offset.setFromSpherical(new THREE.Spherical(from.radius, to.phi, to.theta));
    camera.position.copy(controls.target).add(offset);
    needsRender = true;
    return;
  }
  orbit = { start: performance.now(), from, to };
}

// Distinct shell colors so Wall 1…N read clearly in the size step.
const WALL_SHELL_COLORS = [0xc45c26, 0x2f6f8f, 0x4f8f5a, 0xb8860b, 0x8a5a3c, 0x3d7a8c];
const wallShellMats = new Map();

function wallShellMaterial(index, { active = false } = {}) {
  const key = `${index}:${active ? 1 : 0}`;
  let mat = wallShellMats.get(key);
  if (mat) return mat;
  const color = WALL_SHELL_COLORS[index % WALL_SHELL_COLORS.length];
  mat = new THREE.MeshStandardMaterial({
    color,
    roughness: 0.85,
    metalness: 0,
    transparent: true,
    opacity: active ? 0.92 : 0.55,
    depthWrite: false,
    side: THREE.DoubleSide,
    emissive: active ? color : 0x000000,
    emissiveIntensity: active ? 0.22 : 0,
  });
  wallShellMats.set(key, mat);
  return mat;
}

function wallColorCss(index) {
  return `#${WALL_SHELL_COLORS[index % WALL_SHELL_COLORS.length].toString(16).padStart(6, '0')}`;
}

// Stand outside the chosen wall and look straight at it (front elevation).
let focusedWallIndex = null;

function focusWall(index) {
  if (!camera || !controls) return;
  cancelCameraTween();
  focusedWallIndex = index;
  const plan = isPolyRoom(state) ? roomPlan(state) : null;
  const frame = plan
    ? (plan.frames[index] || plan.byId.front)
    : wallFrame(state.dims, ['front', 'left', 'back', 'right'][index] || 'front');
  if (!frame) return;
  // (nx,nz) should point into the room. Poly winding can invert that — force
  // the origin (room centre) to sit on the inward side before aiming.
  let nx = frame.nx;
  let nz = frame.nz;
  if ((0 - frame.cx) * nx + (0 - frame.cz) * nz <= 0) {
    nx = -nx;
    nz = -nz;
  }
  // Camera on the outward side so this wall sits in the front of the view.
  const azimuthDeg = THREE.MathUtils.radToDeg(Math.atan2(-nx, -nz));
  orbitTo(azimuthDeg, 72);
  if (dom.wallLengths) {
    for (const el of dom.wallLengths.querySelectorAll('.sd-slider')) {
      const i = Number(el.dataset.wallFocus);
      const on = i === index;
      el.classList.toggle('is-active', on);
      el.style.setProperty('--sd-wall-color', wallColorCss(i));
    }
  }
  // Rebuild shell so the active wall reads brighter.
  if (!state.wood.id) scheduleRebuild();
  else needsRender = true;
}

function stepOrbit(now) {
  if (!orbit) return;
  const k = clamp((now - orbit.start) / 900, 0, 1);
  const e = k < 0.5 ? 4 * k * k * k : 1 - Math.pow(-2 * k + 2, 3) / 2;
  const radius = camera.position.distanceTo(controls.target);
  const s = new THREE.Spherical(
    radius,
    THREE.MathUtils.lerp(orbit.from.phi, orbit.to.phi, e),
    THREE.MathUtils.lerp(orbit.from.theta, orbit.to.theta, e),
  );
  camera.position.copy(controls.target).add(new THREE.Vector3().setFromSpherical(s));
  needsRender = true;
  if (k >= 1) orbit = null;
}

function stepCameraTween(now) {
  stepOrbit(now);
  if (!tween) return;
  const k = clamp((now - tween.start) / CONFIG.camera.tweenMs, 0, 1);
  const e = 1 - Math.pow(1 - k, 3);
  const target = tween.fromTarget.clone().lerp(tween.toTarget, e);
  const distance = THREE.MathUtils.lerp(tween.fromDistance, tween.toDistance, e);
  applyCameraFrame(target, distance);
  if (k >= 1) tween = null;
}

function updateWallVisibility() {
  if (!roomGroup) return;
  const p = camera.position;
  const { height: H } = state.dims;
  if (isPolyRoom(state)) {
    const plan = roomPlan(state);
    for (const frame of plan.frames) {
      const g = walls[frame.id];
      if (!g) continue;
      // Keep the focused wall drawn so a head-on edit view shows its face
      // instead of culling it and making the wall feel like it's behind you.
      if (focusedWallIndex === frame.index) {
        g.visible = true;
        continue;
      }
      // Hide a wall when the camera is outside it (on the outward side).
      // Force inward normal so inverted poly winding still culls correctly.
      let nx = frame.nx;
      let nz = frame.nz;
      if ((0 - frame.cx) * nx + (0 - frame.cz) * nz <= 0) {
        nx = -nx;
        nz = -nz;
      }
      const outside = (p.x - frame.cx) * nx + (p.z - frame.cz) * nz < 0;
      g.visible = !outside;
    }
  } else {
    const { length: L, width: W } = state.dims;
    const focusId = focusedWallIndex == null
      ? null
      : ['front', 'left', 'back', 'right'][focusedWallIndex];
    if (walls.front) walls.front.visible = focusId === 'front' || p.z <= W / 2;
    if (walls.back) walls.back.visible = focusId === 'back' || p.z >= -W / 2;
    if (walls.left) walls.left.visible = focusId === 'left' || p.x >= -L / 2;
    if (walls.right) walls.right.visible = focusId === 'right' || p.x <= L / 2;
  }
  if (ceiling) ceiling.visible = p.y <= H;
}

function tick(now) {
  requestAnimationFrame(tick);
  stepCameraTween(now);
  const moved = controls.update();
  if (moved || needsRender) {
    updateWallVisibility();
    renderer.render(scene, camera);
    needsRender = false;
  }
}

/* --------------------------------------------------------------------------
   Rebuild: coalesced to one rebuild per animation frame, so typing and
   slider drags land on the next frame with no queue of stale builds.
   -------------------------------------------------------------------------- */
let rebuildQueued = false;
let lastDims = null;

function scheduleRebuild() {
  renderGate();
  if (rebuildQueued) return;
  rebuildQueued = true;
  requestAnimationFrame(() => {
    rebuildQueued = false;
    rebuild();
  });
}

function rebuild() {
  const previous = roomGroup;
  try {
    syncPolyDims(state);
    state.door.position = doorPosition(state.dims, state.door.placement, state);
    const built = state.wood.id ? buildRoom(state) : buildShell(state);
    if (previous) {
      scene.remove(previous);
      disposeGroup(previous);
    }
    roomGroup = built.group;
    walls = built.walls;
    ceiling = built.ceiling;
    scene.add(roomGroup);
    placeLights(state.dims);
  } catch (err) {
    console.error('[sauna-designer] rebuild failed', err);
    if (!roomGroup && previous) {
      roomGroup = previous;
      scene.add(previous);
    }
    return;
  }

  const dimsKey = [
    ...DIMS.map((d) => state.dims[d]),
    state.wallCount || 4,
    ...(state.walls || []),
  ].join('x');
  if (dimsKey !== lastDims) {
    frameRoom(state.dims);
    lastDims = dimsKey;
  }

  renderFitNotes();
  renderWallCountUI();
  renderSizeUI();
  renderWoodUI();
  renderBenchUI();
  renderFloorUI();
  renderHeaterUI();
  renderDoorUI();
  renderReviewUI();
  renderGate();
  needsRender = true;

  if (new URLSearchParams(window.location.search).get('spec') === '1') {
    console.info('[sauna-designer] spec', computeSpec(state));
  }
}

/* --------------------------------------------------------------------------
   Part 9 — UI binding
   -------------------------------------------------------------------------- */
function renderFitNotes() {
  dom.fitNotes.textContent = '';
  for (const note of checkFit(state)) {
    if (note.ok) continue;
    const li = document.createElement('li');
    li.append(note.message);
    if (note.link) {
      li.append(' ');
      const a = document.createElement('a');
      a.href = note.link.href;
      a.textContent = note.link.text;
      li.append(a);
      li.append('.');
    }
    dom.fitNotes.append(li);
  }
}

// Half-inch steps from the limits in CONFIG. The filled part of the track is
// driven by --sd-fill because WebKit has no native progress pseudo-element.
function syncDim(dim) {
  const { min, max } = CONFIG.limits[dim];
  const value = state.dims[dim];
  const text = formatFtIn(value);
  const slider = dom.sliders.find((el) => el.dataset.dim === dim);
  // Writing .value while a finger is down cancels the drag on iOS.
  if (document.activeElement !== slider) slider.value = String(value);
  slider.setAttribute('aria-valuetext', text);
  slider.style.setProperty('--sd-fill', `${((value - min) / (max - min)) * 100}%`);
  dom.dimInputs.find((el) => el.dataset.dimInput === dim).value = text;
}

function setDim(dim, raw) {
  const { min, max } = CONFIG.limits[dim];
  const value = clamp(roundToStep(raw, CONFIG.dimStep), min, max);
  const changed = state.dims[dim] !== value;
  if (changed) state.dims = { ...state.dims, [dim]: value };
  syncDim(dim);
  if (dim === 'height') syncPolyHeight();
  if (changed) scheduleRebuild();
}

function bindSliders() {
  for (const slider of dom.sliders) {
    const dim = slider.dataset.dim;
    const { min, max } = CONFIG.limits[dim];
    slider.min = String(min);
    slider.max = String(max);
    slider.step = String(CONFIG.dimStep);
    syncDim(dim);
    slider.addEventListener('input', () => setDim(dim, Number(slider.value)));
  }

  for (const input of dom.dimInputs) {
    const dim = input.dataset.dimInput;
    const commit = () => {
      const inches = parseFtIn(input.value);
      if (inches === null) syncDim(dim);
      else setDim(dim, inches);
    };
    input.addEventListener('focus', () => input.select());
    input.addEventListener('change', commit);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        commit();
        input.select();
      } else if (e.key === 'Escape') {
        syncDim(dim);
        input.blur();
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        const step = (e.shiftKey ? 12 : CONFIG.dimStep) * (e.key === 'ArrowUp' ? 1 : -1);
        setDim(dim, state.dims[dim] + step);
        input.select();
      }
    });
  }

  bindPolyHeight();
}

function syncPolyHeight() {
  if (!dom.polyHeight || !dom.polyHeightInput) return;
  const { min, max } = CONFIG.limits.height;
  const value = state.dims.height;
  const text = formatFtIn(value);
  if (document.activeElement !== dom.polyHeight) dom.polyHeight.value = String(value);
  dom.polyHeight.setAttribute('aria-valuetext', text);
  dom.polyHeight.style.setProperty('--sd-fill', `${((value - min) / (max - min)) * 100}%`);
  if (document.activeElement !== dom.polyHeightInput) dom.polyHeightInput.value = text;
}

function bindPolyHeight() {
  if (!dom.polyHeight || !dom.polyHeightInput) return;
  const { min, max } = CONFIG.limits.height;
  dom.polyHeight.min = String(min);
  dom.polyHeight.max = String(max);
  dom.polyHeight.step = String(CONFIG.dimStep);
  syncPolyHeight();
  dom.polyHeight.addEventListener('input', () => setDim('height', Number(dom.polyHeight.value)));
  const commit = () => {
    const inches = parseFtIn(dom.polyHeightInput.value);
    if (inches === null) syncPolyHeight();
    else setDim('height', inches);
  };
  dom.polyHeightInput.addEventListener('focus', () => dom.polyHeightInput.select());
  dom.polyHeightInput.addEventListener('change', commit);
  dom.polyHeightInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
      dom.polyHeightInput.select();
    } else if (e.key === 'Escape') {
      syncPolyHeight();
      dom.polyHeightInput.blur();
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      const step = (e.shiftKey ? 12 : CONFIG.dimStep) * (e.key === 'ArrowUp' ? 1 : -1);
      setDim('height', state.dims.height + step);
      dom.polyHeightInput.select();
    }
  });
}

function setWallLength(index, raw) {
  const value = clampWallLength(raw);
  const next = state.walls.slice();
  while (next.length < state.wallCount) next.push(CONFIG.defaultDims.length);
  const changed = next[index] !== value;
  next[index] = value;
  state.walls = next;
  if (changed) {
    _polyPlanCache.delete(next.map((n) => roundToStep(n, CONFIG.dimStep)).join(','));
    // Drop stale plans so the new lengths rebuild cleanly.
    for (const key of [..._polyPlanCache.keys()]) {
      if (key.split(',').length === next.length) _polyPlanCache.delete(key);
    }
    syncPolyDims();
  }
  syncWallLength(index);
  if (changed) scheduleRebuild();
}

function syncWallLength(index) {
  if (!dom.wallLengths) return;
  const slider = dom.wallLengths.querySelector(`[data-wall-length="${index}"]`);
  const input = dom.wallLengths.querySelector(`[data-wall-length-input="${index}"]`);
  if (!slider || !input) return;
  const { min, max } = CONFIG.limits.length;
  const value = state.walls[index];
  const text = formatFtIn(value);
  if (document.activeElement !== slider) slider.value = String(value);
  slider.setAttribute('aria-valuetext', text);
  slider.style.setProperty('--sd-fill', `${((value - min) / (max - min)) * 100}%`);
  if (document.activeElement !== input) input.value = text;
}

function renderWallLengthControls() {
  if (!dom.wallLengths) return;
  const count = state.wallCount;
  if (!(count > 4)) {
    dom.wallLengths.textContent = '';
    return;
  }
  ensureWallLengths(count);
  const { min, max } = CONFIG.limits.length;
  const existing = dom.wallLengths.childElementCount;
  if (existing !== count) {
    dom.wallLengths.textContent = '';
    for (let i = 0; i < count; i++) {
      const wrap = document.createElement('div');
      wrap.className = 'sd-slider';
      wrap.dataset.wallFocus = String(i);
      wrap.style.setProperty('--sd-wall-color', wallColorCss(i));
      const head = document.createElement('div');
      head.className = 'sd-slider__head';
      const swatch = document.createElement('span');
      swatch.className = 'sd-wall-swatch';
      swatch.setAttribute('aria-hidden', 'true');
      const label = document.createElement('label');
      label.htmlFor = `sd-wall-len-${i}`;
      label.textContent = wallLengthLabel(i);
      label.prepend(swatch);
      const input = document.createElement('input');
      input.type = 'text';
      input.className = 'sd-dim-input';
      input.id = `sd-wall-len-${i}-value`;
      input.dataset.wallLengthInput = String(i);
      input.inputMode = 'decimal';
      input.autocomplete = 'off';
      input.spellcheck = false;
      input.setAttribute('aria-label', `${wallLengthLabel(i)}, in feet and inches`);
      head.append(label, input);
      const slider = document.createElement('input');
      slider.type = 'range';
      slider.className = 'sd-range';
      slider.id = `sd-wall-len-${i}`;
      slider.min = String(min);
      slider.max = String(max);
      slider.step = String(CONFIG.dimStep);
      slider.dataset.wallLength = String(i);
      const scale = document.createElement('div');
      scale.className = 'sd-slider__scale';
      scale.setAttribute('aria-hidden', 'true');
      scale.innerHTML = '<span>3\'</span><span>10\'</span>';
      wrap.append(head, slider, scale);
      dom.wallLengths.append(wrap);

      const activate = () => focusWall(i);
      wrap.addEventListener('pointerdown', activate);
      slider.addEventListener('focus', activate);
      input.addEventListener('focus', () => {
        activate();
        input.select();
      });
      slider.addEventListener('input', () => setWallLength(i, Number(slider.value)));
      const commit = () => {
        const inches = parseFtIn(input.value);
        if (inches === null) syncWallLength(i);
        else setWallLength(i, inches);
      };
      input.addEventListener('change', commit);
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
          input.select();
        } else if (e.key === 'Escape') {
          syncWallLength(i);
          input.blur();
        } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          e.preventDefault();
          const step = (e.shiftKey ? 12 : CONFIG.dimStep) * (e.key === 'ArrowUp' ? 1 : -1);
          setWallLength(i, state.walls[i] + step);
          input.select();
        }
      });
    }
  } else {
    for (let i = 0; i < count; i++) {
      const wrap = dom.wallLengths.querySelector(`[data-wall-focus="${i}"]`);
      if (wrap) wrap.style.setProperty('--sd-wall-color', wallColorCss(i));
      const label = dom.wallLengths.querySelector(`label[for="sd-wall-len-${i}"]`);
      if (label) {
        let swatch = label.querySelector('.sd-wall-swatch');
        if (!swatch) {
          swatch = document.createElement('span');
          swatch.className = 'sd-wall-swatch';
          swatch.setAttribute('aria-hidden', 'true');
          label.prepend(swatch);
        }
        // Keep the number label; swatch is the first child.
        const text = [...label.childNodes].find((n) => n.nodeType === Node.TEXT_NODE);
        if (text) text.textContent = wallLengthLabel(i);
        else label.append(document.createTextNode(wallLengthLabel(i)));
      }
    }
  }
  for (let i = 0; i < count; i++) syncWallLength(i);
}

function renderWallCountUI() {
  if (!dom.wallCounts) return;
  for (const btn of dom.wallCounts.querySelectorAll('[data-wall-count]')) {
    btn.setAttribute('aria-pressed', String(Number(btn.dataset.wallCount) === state.wallCount));
  }
}

function renderSizeUI() {
  const n = state.wallCount;
  const poly = n > 4;
  if (dom.rectDims) dom.rectDims.hidden = poly || !n;
  if (dom.polyDims) dom.polyDims.hidden = !poly;
  if (dom.sizeSub) {
    if (poly) {
      dom.sizeSub.hidden = false;
      dom.sizeSub.textContent = 'Set the ceiling height, then each wall length. Select a wall to view it head-on.';
    } else if (n === 4) {
      dom.sizeSub.hidden = false;
      dom.sizeSub.textContent = 'Length and width are the floor plan; height is the ceiling.';
    } else {
      dom.sizeSub.hidden = true;
      dom.sizeSub.textContent = '';
    }
  }
  if (poly) {
    syncPolyHeight();
    renderWallLengthControls();
  }
}

function setWallCount(count) {
  if (!WALL_COUNT_OPTIONS.includes(count) || state.wallCount === count) return;
  state.wallCount = count;
  ensureWallLengths(count);
  state.door = { ...state.door, wall: 'front' };
  focusedWallIndex = null;
  if (count > 4) syncPolyDims();
  renderWallCountUI();
  renderSizeUI();
  renderGate();
  scheduleRebuild();
}

function bindWallCount() {
  if (!dom.wallCounts) return;
  dom.wallCounts.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wall-count]');
    if (!btn) return;
    setWallCount(Number(btn.dataset.wallCount));
  });
}

// Each step opens once every step before it has its choice. A step that
// cannot be filled for this room (nothing fits) does not hold the rest up.
// Earlier steps stay editable and the build follows them.
// Live markup is the 7-step rectangle flow (no Unique walls). When that step
// is present, shift the checklist so Room size is still index 0 in the UI.
function stepComplete(i) {
  const n = dom.wallCounts ? i : i + 1;
  switch (n) {
    case 0: return WALL_COUNT_OPTIONS.includes(state.wallCount);
    case 1: return true; // room size
    case 2: return !!state.wood.id;
    case 3: return !!state.benches.layout;
    case 4: return !!state.floor;
    case 5: return !!state.heater.id || !heaterFitNote(state).ok;
    case 6: return !!state.door.placement || !doorFitNote(state).ok;
    default: return false;
  }
}

function stepUnlocked(i) {
  for (let j = 0; j < i; j++) if (!stepComplete(j)) return false;
  return true;
}

function renderGate() {
  dom.steps.forEach((el, i) => {
    el.inert = !stepUnlocked(i);
  });
  dom.next.disabled = activeStep === dom.steps.length - 1 || !stepComplete(activeStep);
}

function focusStep(i, { scroll = false } = {}) {
  const step = dom.steps[i];
  if (!step) return;
  const heading = step.querySelector('h2');
  heading.setAttribute('tabindex', '-1');
  heading.focus({ preventScroll: true });
  if (scroll && !isSmallScreen.matches) {
    // scrollIntoView would also scroll the window, so scroll only the panel.
    const panel = step.closest('.sd__panel');
    if (!panel) return;
    const top = step.getBoundingClientRect().top - panel.getBoundingClientRect().top + panel.scrollTop - 24;
    panel.scrollTo({ top: Math.max(0, top), behavior: prefersReducedMotion.matches ? 'auto' : 'smooth' });
  }
}

function renderWoodUI() {
  if (!dom.woods.childElementCount) {
    for (const wood of WOODS) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'sd-option sd-option--wood';
      btn.dataset.woodId = wood.id;
      const swatch = document.createElement('span');
      swatch.className = 'sd-option__swatch';
      swatch.setAttribute('aria-hidden', 'true');
      if (wood.photo) swatch.classList.add('sd-option__swatch--photo');
      swatch.style.backgroundImage = `url(${wood.photo ? assetURL(wood.photo) : woodSwatchURL(wood)})`;
      const name = document.createElement('span');
      name.className = 'sd-option__name';
      name.textContent = wood.name;
      btn.append(swatch, name);
      dom.woods.append(btn);
    }
  }
  for (const btn of dom.woods.children) {
    btn.setAttribute('aria-pressed', String(btn.dataset.woodId === state.wood.id));
  }
}

function bindWoods() {
  renderWoodUI();
  dom.woods.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wood-id]');
    if (!btn || state.wood.id === btn.dataset.woodId) return;
    state.wood = { ...state.wood, id: btn.dataset.woodId };
    scheduleRebuild();
  });
}

function renderFloorUI() {
  dom.floors.forEach((btn) => btn.setAttribute('aria-pressed', String(btn.dataset.floor === state.floor)));
}

function bindFloor() {
  dom.floors.forEach((btn) => btn.addEventListener('click', () => {
    if (state.floor === btn.dataset.floor) return;
    state.floor = btn.dataset.floor;
    scheduleRebuild();
  }));
}

function renderDoorUI() {
  const fit = doorFitNote(state);
  dom.doorNote.hidden = fit.ok;
  dom.doorNote.textContent = fit.ok ? '' : fit.message;
  dom.doorPlacements.forEach((btn) => {
    btn.disabled = !fit.ok;
    btn.setAttribute('aria-pressed', String(btn.dataset.doorPlacement === state.door.placement));
  });
}

/* --------------------------------------------------------------------------
   Review: total, share build, and the price breakdown page
   -------------------------------------------------------------------------- */
const PAGE_CONFIG = (() => {
  try {
    return JSON.parse(document.getElementById('sd-config')?.textContent || '{}');
  } catch (e) {
    return {};
  }
})();
// Theme editor prices arrive as text ("$4.25", "1,250"); coerce every leaf to a number.
function toPrices(value) {
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, toPrices(v)]));
  }
  const n = parseFloat(String(value ?? '').replace(/[$,\s]/g, ''));
  return Number.isFinite(n) ? n : 0;
}
const PRICES = {
  heaters: {}, led: {}, door: 0, vents: 0, vaporRoll: 0, vaporTape: 0, mill: 0, paraffin: 0,
  ...toPrices(PAGE_CONFIG.prices || {}),
};
const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const unitMoney = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });

// "4 boards" and "$35.96 / board" for a line's unit columns.
function unitText(unit) {
  const qty = `${unit.qty.toLocaleString('en-US')} ${unit.qty === 1 ? unit.one : unit.many}`;
  let each = unit.each === undefined ? '' : `${unitMoney.format(unit.each)} / ${unit.one}`;
  if (unit.extra) each += ` + ${money.format(unit.extra.amount)} ${unit.extra.label}`;
  return { qty, each };
}
const STORE_KEY = 'ws-sauna-design';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function renderReviewUI() {
  if (dom.total) dom.total.textContent = money.format(priceDesign(state, PRICES).total);
}

function designURL() {
  const url = new URL(window.location.href);
  const view = url.searchParams.get('view');
  url.search = '';
  url.hash = '';
  if (view) url.searchParams.set('view', view);
  url.searchParams.set('design', encodeDesign(state));
  return url.toString();
}

function breakdownURL() {
  const url = new URL(designURL());
  url.searchParams.set('breakdown', '1');
  return url.toString();
}

function buildTitle() {
  const name = state.owner.trim();
  if (!name) return 'Your WS Custom';
  return `${name}${/s$/i.test(name) ? '’' : '’s'} WS Custom`;
}

function designSummary() {
  const wood = WOODS.find((w) => w.id === state.wood.id);
  const { length: L, width: W, height: H } = state.dims;
  let dimensions = `${formatFtIn(L)} × ${formatFtIn(W)} × ${formatFtIn(H)}`;
  if (state.wallCount > 4 && state.walls.length) {
    const runs = state.walls.map((len, i) => `${wallLengthLabel(i)} ${formatFtIn(len)}`).join(', ');
    dimensions = `${state.wallCount} walls · ${formatFtIn(H)} high · ${runs}`;
  }
  return { dimensions, wood: wood ? wood.name : null };
}

function designEventProps(url) {
  const { lines, total, materials } = priceDesign(state, PRICES);
  return {
    design_url: url,
    build_name: buildTitle(),
    ...designSummary(),
    total,
    total_formatted: money.format(total),
    items: lines.map((l) => ({
      name: l.name,
      detail: l.detail,
      price: l.amount === null ? 'Not included' : money.format(l.amount),
      ...(l.unit ? { quantity: unitText(l.unit).qty, ...(unitText(l.unit).each ? { unit_price: unitText(l.unit).each } : {}) } : {}),
    })),
    boards: materials.flatMap((m) => m.boards.map((b) => ({
      material: m.name, length: `${b.lengthIn}"`, count: b.count, price: money.format(b.price), total: money.format(b.price * b.count),
    }))),
    $value: total,
  };
}

function klaviyoCompanyId() {
  if (PAGE_CONFIG.klaviyoCompanyId) return PAGE_CONFIG.klaviyoCompanyId;
  const script = [...document.scripts].find((s) => /static\.klaviyo\.com\/onsite\/js\/.*company_id=/.test(s.src));
  const match = script && script.src.match(/company_id=([A-Za-z0-9]+)/);
  return match ? match[1] : null;
}

// Klaviyo client Events API. A Klaviyo flow on the metric sends the email.
async function klaviyoEvent(metric, email, properties) {
  const companyId = klaviyoCompanyId();
  if (!companyId) throw new Error('klaviyo-missing');
  const res = await fetch(`https://a.klaviyo.com/client/events/?company_id=${encodeURIComponent(companyId)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
    body: JSON.stringify({
      data: {
        type: 'event',
        attributes: {
          properties,
          metric: { data: { type: 'metric', attributes: { name: metric } } },
          profile: { data: { type: 'profile', attributes: { email } } },
        },
      },
    }),
  });
  if (!res.ok) throw new Error(`klaviyo-${res.status}`);
}

function setStatus(panel, text, isError = false) {
  const el = panel.querySelector('.sd-panel__status');
  el.textContent = text;
  el.classList.toggle('is-error', isError);
}

function rememberDesign() {
  try {
    localStorage.setItem(STORE_KEY, encodeDesign(state));
  } catch (e) { /* private mode */ }
}

// Render one square product shot and read it back straight away, so the
// drawing buffer is still intact without preserveDrawingBuffer. The canvas
// keeps its CSS size, so the live view never flickers.
const CAPTURE = { width: 1400, height: 1400, margin: 0.58, polar: 78 };

// Corners of every piece drawn in the room right now (hidden walls and the
// oversized shadow ground left out). Per piece, so the empty space where
// the hidden walls stand never counts toward the framing.
function visibleRoomPoints() {
  roomGroup.updateMatrixWorld(true);
  const points = [];
  const part = new THREE.Box3();
  roomGroup.traverseVisible((o) => {
    if (!o.isMesh || !o.geometry || o.name === 'ground') return;
    if (!o.geometry.boundingBox) o.geometry.computeBoundingBox();
    part.copy(o.geometry.boundingBox).applyMatrix4(o.matrixWorld);
    for (const x of [part.min.x, part.max.x]) for (const y of [part.min.y, part.max.y]) for (const z of [part.min.z, part.max.z]) points.push(new THREE.Vector3(x, y, z));
  });
  return points;
}

// Straight into the open front, a little above the benches, like a product photo.
function captureViewDir() {
  const wall = state.door.wall || 'front';
  // Azimuth is measured from +z (the front wall) toward +x (the right wall).
  const towardDoor = { front: 0, right: 90, back: 180, left: -90 }[wall] ?? 0;
  return new THREE.Vector3().setFromSpherical(new THREE.Spherical(
    1,
    THREE.MathUtils.degToRad(CAPTURE.polar),
    THREE.MathUtils.degToRad(towardDoor),
  )).normalize();
}

// Fixed door-facing angle, moved and pulled in until every corner of the build
// sits inside the frame with a margin all round.
function frameCaptureCamera(dir) {
  const target = controls.target.clone();
  let dist = fitDistance(state.dims);
  camera.position.copy(target).addScaledVector(dir, dist);
  camera.lookAt(target);
  updateWallVisibility();
  const corners = visibleRoomPoints();
  if (!corners.length) return;
  target.copy(new THREE.Box3().setFromPoints(corners).getCenter(new THREE.Vector3()));
  const right = new THREE.Vector3();
  const up = new THREE.Vector3();
  const p = new THREE.Vector3();
  const halfV = Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2);
  for (let i = 0; i < 8; i++) {
    camera.position.copy(target).addScaledVector(dir, dist);
    camera.lookAt(target);
    camera.updateMatrixWorld();
    let x0 = Infinity, x1 = -Infinity, y0 = Infinity, y1 = -Infinity;
    for (const c of corners) {
      p.copy(c).project(camera);
      x0 = Math.min(x0, p.x); x1 = Math.max(x1, p.x);
      y0 = Math.min(y0, p.y); y1 = Math.max(y1, p.y);
    }
    right.setFromMatrixColumn(camera.matrixWorld, 0);
    up.setFromMatrixColumn(camera.matrixWorld, 1);
    target.addScaledVector(right, ((x0 + x1) / 2) * dist * halfV * camera.aspect);
    target.addScaledVector(up, ((y0 + y1) / 2) * dist * halfV);
    dist *= Math.max((x1 - x0) / 2, (y1 - y0) / 2) / CAPTURE.margin;
  }
  // Leave a band above the sauna for the studio name.
  target.addScaledVector(up, 0.1 * dist * halfV);
  camera.position.copy(target).addScaledVector(dir, dist);
  camera.lookAt(target);
  updateWallVisibility();
}

// The shared still: the front product shot, with the studio name on it.
function stampStudioShot(glCanvas) {
  const out = document.createElement('canvas');
  out.width = glCanvas.width;
  out.height = glCanvas.height;
  const ctx = out.getContext('2d');
  ctx.drawImage(glCanvas, 0, 0);
  const mark = 'Western Sol Customs Studio';
  const size = Math.round(out.width * 0.034);
  ctx.fillStyle = '#161616';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  ctx.font = `600 ${size}px "Helvetica Neue", Helvetica, Arial, sans-serif`;
  const y = Math.round(out.height * 0.09);
  const half = ctx.measureText(mark).width / 2;
  ctx.fillText(mark, out.width / 2, y);
  ctx.font = `600 ${Math.round(size * 0.42)}px "Helvetica Neue", Helvetica, Arial, sans-serif`;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.fillText('™', out.width / 2 + half + size * 0.08, y - size * 0.95);
  return out.toDataURL('image/jpeg', 0.92);
}

function captureBuild() {
  if (!renderer || !roomGroup) return null;
  const size = renderer.getSize(new THREE.Vector2());
  const ratio = renderer.getPixelRatio();
  const livePosition = camera.position.clone();
  const liveQuaternion = camera.quaternion.clone();
  const clear = new THREE.Color();
  renderer.getClearColor(clear);
  const clearAlpha = renderer.getClearAlpha();
  let shot = null;
  renderer.setClearColor(0xf4f4f4, 1);
  renderer.setPixelRatio(1);
  renderer.setSize(CAPTURE.width, CAPTURE.height, false);
  camera.aspect = CAPTURE.width / CAPTURE.height;
  camera.updateProjectionMatrix();
  frameCaptureCamera(captureViewDir());
  renderer.render(scene, camera);
  try {
    shot = stampStudioShot(renderer.domElement);
  } catch (e) { /* tainted or lost context */ }
  camera.position.copy(livePosition);
  camera.quaternion.copy(liveQuaternion);
  updateWallVisibility();
  renderer.setClearColor(clear, clearAlpha);
  renderer.setPixelRatio(ratio);
  renderer.setSize(size.x, size.y, false);
  camera.aspect = size.x / size.y;
  camera.updateProjectionMatrix();
  needsRender = true;
  return shot;
}

function renderBreakdown() {
  const { lines, total } = priceDesign(state, PRICES);
  const { dimensions, wood } = designSummary();
  dom.bdTitle.textContent = buildTitle();
  dom.bdSummary.textContent = dimensions;
  dom.bdTotal.textContent = money.format(total);
  dom.bdLines.textContent = '';
  for (const line of lines) {
    const li = document.createElement('li');
    li.className = line.amount === null ? 'sd-bd__line sd-bd__line--off' : 'sd-bd__line';
    const name = document.createElement('span');
    name.className = 'sd-bd__line-name';
    name.textContent = line.name;
    const price = document.createElement('span');
    price.className = 'sd-bd__line-price';
    price.textContent = line.amount === null ? 'Not included' : money.format(line.amount);
    const detail = document.createElement('span');
    detail.className = 'sd-bd__line-detail';
    detail.textContent = line.detail;
    li.append(name, price, detail);
    if (line.unit) {
      const { qty, each } = unitText(line.unit);
      const units = document.createElement('span');
      units.className = 'sd-bd__line-units';
      const q = document.createElement('span');
      q.className = 'sd-bd__line-qty';
      q.textContent = qty;
      units.append(q);
      if (each) {
        const e = document.createElement('span');
        e.className = 'sd-bd__line-each';
        e.textContent = each;
        units.append(e);
      }
      li.append(units);
    }
    dom.bdLines.append(li);
  }
}

function calendarSrc(raw) {
  try {
    const url = new URL(raw, window.location.href);
    if (url.hostname === 'calendar.google.com' && !url.searchParams.has('gv')) url.searchParams.set('gv', 'true');
    return url.toString();
  } catch (e) {
    return raw;
  }
}

// Google's booking page can't be styled from here, so the embed is cropped
// to its calendar card: the frame is snapped to a measured width (kept under
// 600px so Google uses its stacked layout) and slid up and sideways inside a
// clipped box. Offsets were measured on the WS Sauna Consult page and change
// if its title or description changes.
// [frame width from, px above the month header, card bottom, footer height]
const CAL_CROP = [[280, 460, 1067, 144], [300, 415, 1023, 144], [320, 415, 999, 128], [360, 415, 999, 112], [400, 386, 970, 112], [540, 366, 950, 112]];
const CAL_DIALOG_H = 560; // Google's booking form, centred in the frame
const CAL_SIDE = 9; // card border plus Google's page margin
let calendarOpened = false;

function cropCalendar() {
  const frame = dom.bdCalendar;
  const box = frame && frame.parentElement;
  if (!box || !frame.hasAttribute('data-crop') || !box.clientWidth) return;
  const avail = box.clientWidth;
  const width = Math.min(580, Math.max(280, Math.ceil((avail + 2 * CAL_SIDE) / 20) * 20));
  const [, top, cardBottom, footer] = CAL_CROP.reduce((row, next) => (width >= next[0] ? next : row), CAL_CROP[0]);
  // Until someone uses it, the box ends at the card. Once they click in,
  // picked times stretch the card and the booking form is centred in the
  // whole frame, so the box grows to fit the form.
  const fitted = cardBottom - 1 - top;
  const visible = calendarOpened ? Math.max(fitted, top + CAL_DIALOG_H - footer) : fitted;
  box.classList.add('is-cropped');
  box.style.height = `${visible}px`;
  frame.style.width = `${width}px`;
  frame.style.height = `${top + visible + footer + 4}px`;
  frame.style.top = `${-top}px`;
  frame.style.left = `${(avail - (width - 2 * CAL_SIDE)) / 2 - CAL_SIDE}px`;
}

function watchCalendarUse() {
  window.addEventListener('blur', () => {
    if (calendarOpened || document.activeElement !== dom.bdCalendar) return;
    calendarOpened = true;
    cropCalendar();
  });
}

function breakdownIsOpen() {
  return !dom.breakdown.hidden;
}

function openBreakdown({ push = true } = {}) {
  if (!stepUnlocked(dom.steps.length - 1)) return;
  const shot = captureBuild();
  dom.bdFigure.classList.toggle('is-empty', !shot);
  dom.bdShot.hidden = !shot;
  if (shot) dom.bdShot.src = shot;
  renderBreakdown();
  if (dom.bdCalendar && !dom.bdCalendar.src) {
    dom.bdCalendar.src = calendarSrc(dom.bdCalendar.dataset.src);
    if (dom.bdCalendar.hasAttribute('data-crop')) {
      new ResizeObserver(cropCalendar).observe(dom.bdCalendar.parentElement);
      watchCalendarUse();
    }
  }
  rememberDesign();
  if (push) window.history.pushState({ sdBreakdown: true }, '', breakdownURL());
  dom.section.classList.add('is-breakdown');
  dom.breakdown.hidden = false;
  dom.breakdown.scrollTop = 0;
  window.scrollTo(0, 0);
  dom.bdTitle.focus({ preventScroll: true });
}

function closeBreakdown() {
  dom.section.classList.remove('is-breakdown');
  dom.breakdown.hidden = true;
  needsRender = true;
  dom.breakdownOpen.focus({ preventScroll: true });
}

// Send the build to other people: emails are collected as chips, and each
// one gets a Klaviyo event; a Klaviyo flow on the metric sends the email.
function bindExport() {
  if (!dom.exportDialog) return;
  const recipients = [];
  const form = dom.exportForm;

  const renderChips = () => {
    dom.exportChips.querySelectorAll('.sd-chip').forEach((chip) => chip.remove());
    for (const email of recipients) {
      const chip = document.createElement('span');
      chip.className = 'sd-chip';
      chip.textContent = email;
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.textContent = '×';
      remove.setAttribute('aria-label', `Remove ${email}`);
      remove.addEventListener('click', () => {
        recipients.splice(recipients.indexOf(email), 1);
        renderChips();
        dom.exportEmail.focus();
      });
      chip.append(remove);
      dom.exportChips.insertBefore(chip, dom.exportEmail);
    }
  };

  // Adds every valid email in the text; returns what could not be read.
  const addEmails = (text) => {
    const bad = [];
    for (const raw of text.split(/[\s,;]+/)) {
      const email = raw.trim().toLowerCase();
      if (!email) continue;
      if (!EMAIL_RE.test(email)) bad.push(raw);
      else if (!recipients.includes(email)) recipients.push(email);
    }
    renderChips();
    return bad.join(' ');
  };

  dom.exportOpen.addEventListener('click', () => {
    setStatus(form, '');
    dom.exportDialog.showModal();
    dom.exportEmail.focus();
  });
  dom.exportClose.addEventListener('click', () => dom.exportDialog.close());
  dom.exportDialog.addEventListener('click', (e) => {
    if (e.target === dom.exportDialog) dom.exportDialog.close();
  });
  dom.exportChips.addEventListener('click', (e) => {
    if (e.target === dom.exportChips) dom.exportEmail.focus();
  });

  dom.exportEmail.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      if (!dom.exportEmail.value.trim()) return;
      e.preventDefault();
      dom.exportEmail.value = addEmails(dom.exportEmail.value);
      setStatus(form, dom.exportEmail.value ? 'That doesn\'t look like an email address.' : '', !!dom.exportEmail.value);
    } else if (e.key === 'Backspace' && !dom.exportEmail.value && recipients.length) {
      recipients.pop();
      renderChips();
    }
  });
  dom.exportEmail.addEventListener('paste', (e) => {
    const text = e.clipboardData?.getData('text') || '';
    if (!/[\s,;]/.test(text.trim())) return;
    e.preventDefault();
    dom.exportEmail.value = addEmails(text);
  });
  dom.exportEmail.addEventListener('blur', () => {
    if (dom.exportEmail.value.trim()) dom.exportEmail.value = addEmails(dom.exportEmail.value);
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const leftover = addEmails(dom.exportEmail.value);
    dom.exportEmail.value = leftover;
    if (leftover) {
      setStatus(form, `"${leftover}" doesn't look like an email address.`, true);
      return;
    }
    if (!recipients.length) {
      setStatus(form, 'Add at least one email to send to.', true);
      return;
    }
    const button = form.querySelector('[type=submit]');
    button.disabled = true;
    setStatus(form, 'Sending…');
    rememberDesign();
    const sender = form.elements.sender.value.trim();
    const { $value, ...design } = designEventProps(breakdownURL());
    const props = { ...design, shared_by: sender || null };
    const sending = [...recipients];
    const results = await Promise.allSettled(sending.map((email) => klaviyoEvent('Shared Sauna Design', email, props)));
    const failed = sending.filter((_, i) => results[i].status === 'rejected');
    recipients.splice(0, recipients.length, ...failed);
    renderChips();
    button.disabled = false;
    if (!failed.length) {
      setStatus(form, `Sent to ${joinAnd(sending)}.`);
    } else if (failed.length === sending.length) {
      setStatus(form, 'That did not go through. Check the emails and try again.', true);
    } else {
      setStatus(form, `Sent to ${joinAnd(sending.filter((m) => !failed.includes(m)))}. ${joinAnd(failed)} did not go through; try again.`, true);
    }
  });
}

// HubSpot lead form on step 7. The form lives in a HubSpot iframe, so button
// color and label are set in HubSpot. On a successful submit we open the
// price breakdown and fire the Klaviyo "Saved Sauna Design" event that
// starts the saved-build flow.
function bindHubSpotLead() {
  const root = document.getElementById('sd-hubspot');
  if (!root) return;

  // Both HubSpot listeners can fire for one submit; send the event once.
  let lastSentKey = '';
  let lastSentAt = 0;

  const fieldValue = (values, ...keys) => {
    if (!values || typeof values !== 'object') return '';
    for (const key of keys) {
      const raw = values[key];
      if (typeof raw === 'string' && raw.trim()) return raw.trim();
      if (Array.isArray(raw) && typeof raw[0] === 'string' && raw[0].trim()) return raw[0].trim();
    }
    return '';
  };

  const takeName = (values = {}) => {
    const name = fieldValue(values, 'firstname', 'firstName', 'first_name', 'name', 'fullname', 'fullname_name');
    if (name) {
      state.owner = name.slice(0, 40);
      if (breakdownIsOpen()) renderBreakdown();
    }
  };

  const takeEmail = (values = {}) => fieldValue(values, 'email', 'Email', 'email_address', 'emailAddress').toLowerCase();

  const sendSavedEvent = async (email) => {
    const key = `${email}|${encodeDesign(state)}`;
    const now = Date.now();
    if (key === lastSentKey && now - lastSentAt < 8000) return;
    lastSentKey = key;
    lastSentAt = now;
    try {
      await klaviyoEvent('Saved Sauna Design', email, { ...designEventProps(designURL()), maker_email: email });
      console.info('[sauna-designer] Klaviyo "Saved Sauna Design" sent for', email);
      try { window.klaviyo?.identify?.({ email }); } catch (err) { /* optional */ }
    } catch (err) {
      console.error('[sauna-designer] Saved Sauna Design event failed', err);
      if (lastSentKey === key) {
        lastSentKey = '';
        lastSentAt = 0;
      }
    }
  };

  // HubSpot hands back field values in different shapes depending on the
  // form version. New forms (the hs-form-frame embed) send only formId and
  // instanceId with the success event; the values have to be fetched from
  // HubSpotFormsV4, and their names carry an object prefix ("0-1/email").
  // Legacy forms post an array of { name, value } or a plain object.
  // Everything is flattened to { email: '...', firstname: '...' }.
  const normalizeValues = (raw) => {
    const out = {};
    const put = (key, value) => {
      if (key) out[String(key).replace(/^\d+-\d+\//, '')] = value;
    };
    if (Array.isArray(raw)) raw.forEach((field) => field && put(field.name, field.value));
    else if (raw && typeof raw === 'object') Object.entries(raw).forEach(([k, v]) => put(k, v));
    return out;
  };

  const readV4Values = async (event) => {
    try {
      const form = window.HubSpotFormsV4?.getFormFromEvent?.(event);
      if (form?.getFormFieldValues) return normalizeValues(await form.getFormFieldValues());
    } catch (err) {
      console.warn('[sauna-designer] Could not read HubSpot field values', err);
    }
    return {};
  };

  const onSuccess = (values) => {
    takeName(values);
    rememberDesign();
    if (!breakdownIsOpen()) openBreakdown();
    const email = takeEmail(values);
    if (EMAIL_RE.test(email)) {
      sendSavedEvent(email);
    } else {
      console.warn('[sauna-designer] HubSpot form submitted but no email was found, so the Klaviyo "Saved Sauna Design" event was not sent. Values seen:', values);
    }
  };

  window.addEventListener('hs-form-event:on-submission:success', async (event) => {
    onSuccess(await readV4Values(event));
  });
  window.addEventListener('message', (event) => {
    const data = event.data;
    if (!data || typeof data !== 'object') return;
    if (data.type === 'hsFormCallback' && data.eventName === 'onFormSubmitted') {
      const payload = data.data || {};
      onSuccess(normalizeValues(payload.submissionValues || payload.values || payload));
    }
  });
}

function bindReview() {
  dom.owner?.addEventListener('input', () => {
    state.owner = dom.owner.value.trim();
    if (breakdownIsOpen()) renderBreakdown();
  });

  async function saveDesign(form) {
    const email = form.elements.email.value.trim();
    if (!EMAIL_RE.test(email)) {
      setStatus(form, 'Enter an email.', true);
      return;
    }
    const button = form.querySelector('[type=submit]');
    button.disabled = true;
    setStatus(form, 'Sending…');
    rememberDesign();
    try {
      await klaviyoEvent('Saved Sauna Design', email, { ...designEventProps(designURL()), maker_email: email });
      try { window.klaviyo?.identify?.({ email }); } catch (err) { /* optional */ }
      setStatus(form, `Saved. The link is on its way to ${email}.`);
    } catch (err) {
      setStatus(form, 'That did not go through. Check your email address and try again.', true);
    } finally {
      button.disabled = false;
    }
  }

  dom.shareToggle?.addEventListener('click', () => {
    const open = dom.share.hidden;
    dom.share.hidden = !open;
    dom.shareToggle.setAttribute('aria-expanded', String(open));
    if (open) dom.share.elements.email.focus({ preventScroll: true });
  });

  dom.share?.addEventListener('submit', (e) => {
    e.preventDefault();
    saveDesign(dom.share);
  });

  dom.bdSaveToggle?.addEventListener('click', () => {
    dom.bdSaveToggle.hidden = true;
    dom.bdSave.hidden = false;
    if (dom.share?.elements?.email?.value && !dom.bdSave.elements.email.value) {
      dom.bdSave.elements.email.value = dom.share.elements.email.value;
    }
    dom.bdSave.elements.email.focus();
  });
  dom.bdSave?.addEventListener('submit', (e) => {
    e.preventDefault();
    saveDesign(dom.bdSave);
  });

  bindHubSpotLead();
  bindExport();
  dom.breakdownOpen?.addEventListener('click', () => openBreakdown());
  dom.breakdownClose.addEventListener('click', () => {
    if (window.history.state?.sdBreakdown) {
      window.history.back();
    } else {
      window.history.replaceState(null, '', designURL());
      closeBreakdown();
    }
  });
  window.addEventListener('popstate', () => {
    const wantsBreakdown = new URLSearchParams(window.location.search).get('breakdown') === '1';
    if (wantsBreakdown && !breakdownIsOpen()) openBreakdown({ push: false });
    if (!wantsBreakdown && breakdownIsOpen()) closeBreakdown();
  });
}

// A saved or shared link opens straight onto the review step.
function restoreFromLink() {
  const code = new URLSearchParams(window.location.search).get('design');
  const d = code && decodeDesign(code);
  if (!d) return false;
  state.wallCount = d.wallCount;
  state.walls = d.walls || [];
  state.dims = d.dims;
  state.wood = { ...state.wood, id: d.woodId };
  state.benches = { ...state.benches, layout: d.layout, lSide: d.lSide, stepSide: d.stepSide, support: d.support };
  state.lighting = { ...state.lighting, underBench: d.underBench };
  state.floor = d.floor;
  state.heater = { ...state.heater, id: d.heaterId, wall: d.heaterWall, position: d.heaterPos };
  state.door = { ...state.door, placement: d.door };
  state.owner = d.owner;
  if (dom.owner) dom.owner.value = d.owner;
  DIMS.forEach(syncDim);
  syncPolyHeight();
  renderWallCountUI();
  renderSizeUI();
  return true;
}

function bindDoor() {
  dom.doorPlacements.forEach((btn) => btn.addEventListener('click', () => {
    if (btn.disabled || state.door.placement === btn.dataset.doorPlacement) return;
    const first = !state.door.placement;
    state.door = { ...state.door, placement: btn.dataset.doorPlacement };
    scheduleRebuild();
    if (first) orbitTo(180 + CONFIG.camera.startAzimuth, 62);
  }));
}

// Isometric icon for a bench layout, seen from the front corner like the 3D
// view: back wall on the right, side wall on the left. Room units: x along
// the back wall, y out from it, z up. Boxes are [x0, x1, y0, y1, z] and are
// listed back to front so later ones paint over earlier ones.
const ICON_ROOM = { w: 10, d: 8, h: 5 };
const ICON_SEAT = { low: 1.3, mid: 2.6, high: 3.9 };
const BENCH_ICON_BOXES = {
  'two-tier': [[0, 10, 0, 2, 'mid'], [0, 10, 2, 4, 'low']],
  'l-shape': [[0, 10, 0, 2, 'low'], [0, 2, 2, 8, 'low']],
  'l-step': [[0, 10, 0, 2, 'mid'], [0, 2, 2, 8, 'mid'], [2, 4, 2, 8, 'low']],
  'three-tier': [[0, 10, 0, 2, 'high'], [0, 10, 2, 4, 'mid'], [0, 10, 4, 6, 'low']],
};
function benchLayoutIcon(id) {
  const pt = (x, y, z) => `${((x - y) * 0.866).toFixed(2)},${((x + y) * 0.5 - z).toFixed(2)}`;
  const poly = (pts, fill) => `<polygon points="${pts.map((p) => pt(...p)).join(' ')}" fill="${fill}"/>`;
  const { w, d, h } = ICON_ROOM;
  let shapes = poly([[0, 0, 0], [w, 0, 0], [w, d, 0], [0, d, 0]], '#e5e5ea')
    + poly([[0, 0, 0], [w, 0, 0], [w, 0, h], [0, 0, h]], '#d1d1d6')
    + poly([[0, 0, 0], [0, d, 0], [0, d, h], [0, 0, h]], '#c7c7cc');
  for (const [x0, x1, y0, y1, seat] of BENCH_ICON_BOXES[id] || []) {
    const z = ICON_SEAT[seat];
    shapes += poly([[x0, y1, 0], [x1, y1, 0], [x1, y1, z], [x0, y1, z]], '#5a5a5e')
      + poly([[x1, y0, 0], [x1, y1, 0], [x1, y1, z], [x1, y0, z]], '#3a3a3c')
      + poly([[x0, y0, z], [x1, y0, z], [x1, y1, z], [x0, y1, z]], '#8e8e93');
  }
  return `<svg class="sd-option__icon" viewBox="-7.4 -5.4 16.8 14.8" aria-hidden="true">`
    + `<g stroke="#fff" stroke-width="0.12" stroke-linejoin="round">${shapes}</g></svg>`;
}

// Layouts that fit this room, plus the chosen one if the room has since
// shrunk past it, so the choice stays visible with its fit note.
function renderBenchUI() {
  const { layout } = state.benches;
  const fitting = benchChoices(state);
  const shown = BENCH_LAYOUTS.filter((l) => fitting.includes(l) || l.id === layout);
  const key = shown.map((l) => l.id).join();
  if (dom.benchLayouts.dataset.key !== key) {
    dom.benchLayouts.textContent = '';
    for (const l of shown) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'sd-option sd-option--layout';
      btn.dataset.benchLayout = l.id;
      const name = document.createElement('span');
      name.className = 'sd-option__name';
      name.textContent = l.name;
      btn.append(name);
      btn.insertAdjacentHTML('beforeend', benchLayoutIcon(l.id));
      dom.benchLayouts.append(btn);
    }
    dom.benchLayouts.dataset.key = key;
  }
  for (const btn of dom.benchLayouts.children) {
    btn.setAttribute('aria-pressed', String(btn.dataset.benchLayout === layout));
  }

  dom.benchToggles.hidden = !layout;
  dom.supports.forEach((btn) => btn.setAttribute('aria-pressed', String(btn.dataset.support === state.benches.support)));
  dom.leds.forEach((btn) => btn.setAttribute('aria-pressed', String((btn.dataset.led === 'on') === state.lighting.underBench)));
  const isL = !!layout && layout.startsWith('l-');
  dom.lSideField.hidden = !isL;
  const side = resolveLSide(state);
  dom.lSides.forEach((btn) => btn.setAttribute('aria-pressed', String(btn.dataset.lside === side)));
  const fit = checkFit(state).find((n) => n.id === 'benches');
  dom.benchNote.hidden = fit.ok;
  dom.benchNote.textContent = fit.ok ? '' : fit.message;
}

function bindBenches() {
  dom.benchLayouts.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-bench-layout]');
    if (!btn || state.benches.layout === btn.dataset.benchLayout) return;
    state.benches = { ...state.benches, layout: btn.dataset.benchLayout };
    scheduleRebuild();
  });
  dom.supports.forEach((btn) => btn.addEventListener('click', () => {
    if (state.benches.support === btn.dataset.support) return;
    state.benches = { ...state.benches, support: btn.dataset.support };
    scheduleRebuild();
  }));
  dom.leds.forEach((btn) => btn.addEventListener('click', () => {
    const on = btn.dataset.led === 'on';
    if (state.lighting.underBench === on) return;
    state.lighting = { ...state.lighting, underBench: on };
    scheduleRebuild();
  }));
  dom.lSides.forEach((btn) => btn.addEventListener('click', () => {
    if (state.benches.lSide === btn.dataset.lside) return;
    state.benches = { ...state.benches, lSide: btn.dataset.lside };
    scheduleRebuild();
  }));
}

// Same corner view as the bench icons: the heater against the back wall of a
// small room slice, drawn to its real size (one unit is 14").
function heaterIcon(heater) {
  const pt = (x, y, z) => `${((x - y) * 0.866).toFixed(2)},${((x + y) * 0.5 - z).toFixed(2)}`;
  const poly = (pts, fill) => `<polygon points="${pts.map((p) => pt(...p)).join(' ')}" fill="${fill}"/>`;
  const box = (x0, x1, y0, y1, z0, z1, [front, side, top]) => poly([[x0, y1, z0], [x1, y1, z0], [x1, y1, z1], [x0, y1, z1]], front)
    + poly([[x1, y0, z0], [x1, y1, z0], [x1, y1, z1], [x1, y0, z1]], side)
    + poly([[x0, y0, z1], [x1, y0, z1], [x1, y1, z1], [x0, y1, z1]], top);
  const unit = 14;
  const [w, d, h] = [heater.w / unit, heater.d / unit, heater.h / unit];
  const room = { w: 4, d: 3.4, h: 6 };
  const x0 = (room.w - w) / 2;
  const body = ['#5a5a5e', '#3a3a3c', '#8e8e93'];
  let shapes = poly([[0, 0, 0], [room.w, 0, 0], [room.w, room.d, 0], [0, room.d, 0]], '#e5e5ea')
    + poly([[0, 0, 0], [room.w, 0, 0], [room.w, 0, room.h], [0, 0, room.h]], '#d1d1d6')
    + poly([[0, 0, 0], [0, room.d, 0], [0, room.d, room.h], [0, 0, room.h]], '#c7c7cc');
  if (heater.style === 'stones') {
    const bodyH = h * 0.62;
    shapes += box(x0, x0 + w, 0, d, 0, bodyH, body);
    const stones = [[0.25, 0.3], [0.7, 0.25], [0.45, 0.55], [0.2, 0.75], [0.75, 0.7], [0.5, 0.9]];
    for (const [u, v] of stones) {
      const [cx, cy] = pt(x0 + u * w, v * d, bodyH + 0.12).split(',');
      shapes += `<ellipse cx="${cx}" cy="${cy}" rx="0.32" ry="0.22" fill="#aeaeb2"/>`;
    }
  } else {
    shapes += box(x0, x0 + w, 0, d, 0, h, body);
    const inset = w * 0.18;
    shapes += poly([[x0 + inset, d, h * 0.12], [x0 + w - inset, d, h * 0.12], [x0 + w - inset, d, h * 0.78], [x0 + inset, d, h * 0.78]], '#2c2c2e');
  }
  return `<svg class="sd-option__icon" viewBox="-3.1 -6.2 6.8 10.2" aria-hidden="true">`
    + `<g stroke="#fff" stroke-width="0.1" stroke-linejoin="round">${shapes}</g></svg>`;
}

function heaterCard(heater, { recommended, selected }) {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'sd-option sd-option--layout';
  btn.dataset.heaterId = heater.id;
  btn.setAttribute('aria-pressed', String(selected));
  const name = document.createElement('span');
  name.className = 'sd-option__name';
  name.textContent = heater.name;
  btn.append(name);
  if (recommended) {
    const tag = document.createElement('span');
    tag.className = 'sd-option__tag';
    tag.textContent = 'Recommended';
    btn.append(tag);
  }
  btn.insertAdjacentHTML('beforeend', heaterIcon(heater));
  return btn;
}

function syncHeaterWallButtons() {
  const host = dom.heaterWalls[0]?.parentElement;
  if (!host) return;
  const ids = wallIds(state);
  const current = Array.from(host.querySelectorAll('[data-heater-wall]')).map((b) => b.dataset.heaterWall);
  if (current.length === ids.length && current.every((id, i) => id === ids[i])) {
    dom.heaterWalls = Array.from(host.querySelectorAll('[data-heater-wall]'));
    return;
  }
  host.textContent = '';
  for (const id of ids) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.dataset.heaterWall = id;
    btn.setAttribute('aria-pressed', 'false');
    btn.textContent = wallLabel(state, id);
    host.append(btn);
  }
  dom.heaterWalls = Array.from(host.querySelectorAll('[data-heater-wall]'));
}

function renderHeaterUI() {
  const choices = heaterChoices(state);
  const placed = resolveHeater(state);
  const recommendedId = heaterOptions(roomVolumeCF(state.dims, 0, state)).recommended?.id;

  dom.heaters.textContent = '';
  for (const heater of choices) {
    dom.heaters.append(heaterCard(heater, {
      recommended: heater.id === recommendedId,
      selected: !!state.heater.id && !!placed && placed.heater.id === heater.id,
    }));
  }

  const fit = heaterFitNote(state);
  dom.heaterNote.hidden = fit.ok;
  dom.heaterNote.textContent = fit.ok ? '' : fit.message;
  if (!fit.ok && fit.link) {
    const a = document.createElement('a');
    a.href = fit.link.href;
    a.textContent = fit.link.text;
    dom.heaterNote.append(' ', a, '.');
  }

  syncHeaterWallButtons();
  const heater = placed?.heater || choices[0] || null;
  for (const btn of dom.heaterWalls) {
    const id = btn.dataset.heaterWall;
    btn.textContent = wallLabel(state, id);
    btn.disabled = !heater || heaterSpans(state, heater, id).length === 0;
    btn.setAttribute('aria-pressed', String(!!placed && id === placed.wall));
  }

  dom.heaterPlace.hidden = !placed || !state.heater.id;
  if (!placed || !state.heater.id) return;

  const position = Math.round(clamp(state.heater.position, 0, 1) * 100);
  if (document.activeElement !== dom.heaterPosition) dom.heaterPosition.value = String(position);
  dom.heaterPosition.style.setProperty('--sd-fill', `${position}%`);
  dom.heaterPosition.disabled = placed.spans.length === 1 && placed.spans[0][1] - placed.spans[0][0] < 0.5;
}

function bindHeater() {
  dom.heaters.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-heater-id]');
    if (!btn || state.heater.id === btn.dataset.heaterId) return;
    state.heater = { ...state.heater, id: btn.dataset.heaterId };
    scheduleRebuild();
  });
  const host = dom.heaterWalls[0]?.parentElement;
  host?.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-heater-wall]');
    if (!btn || btn.disabled) return;
    state.heater = { ...state.heater, wall: btn.dataset.heaterWall };
    scheduleRebuild();
  });
  dom.heaterPosition.addEventListener('input', () => {
    state.heater = { ...state.heater, position: Number(dom.heaterPosition.value) / 100 };
    scheduleRebuild();
  });
}

let activeStep = 0;

function showStep(index) {
  activeStep = clamp(index, 0, dom.steps.length - 1);
  dom.steps.forEach((el, i) => el.classList.toggle('is-active', i === activeStep));
  dom.back.disabled = activeStep === 0;
  dom.stepCount.textContent = `Step ${activeStep + 1} of ${dom.steps.length}`;
  renderGate();
}

function bindStepper() {
  dom.back.addEventListener('click', () => showStep(activeStep - 1));
  dom.next.addEventListener('click', () => {
    if (!stepComplete(activeStep)) return;
    showStep(activeStep + 1);
    focusStep(activeStep);
  });
  showStep(0);
}

/* --------------------------------------------------------------------------
   Part 10 — Boot
   -------------------------------------------------------------------------- */
function showFallback() {
  dom.loading.hidden = true;
  dom.fallback.hidden = false;
}

// Each toggle gets one white thumb that slides to whichever button is
// pressed. It follows aria-pressed, so the render functions need no changes,
// and re-measures when a toggle is shown or resized.
function bindSegmentThumbs() {
  for (const seg of document.querySelectorAll('.sd-segment')) {
    if (seg.querySelector('.sd-segment__thumb')) continue;
    const thumb = document.createElement('span');
    thumb.className = 'sd-segment__thumb';
    thumb.setAttribute('aria-hidden', 'true');
    seg.prepend(thumb);
    seg.classList.add('sd-segment--thumb');
    const place = () => {
      const btn = seg.querySelector('button[aria-pressed="true"]');
      if (!btn || !btn.offsetWidth) {
        thumb.style.opacity = '0';
        return;
      }
      const moved = thumb.style.opacity === '1';
      if (!moved) thumb.style.transition = 'none';
      thumb.style.opacity = '1';
      thumb.style.width = `${btn.offsetWidth}px`;
      thumb.style.height = `${btn.offsetHeight}px`;
      thumb.style.transform = `translate(${btn.offsetLeft}px, ${btn.offsetTop}px)`;
      if (!moved) {
        thumb.getBoundingClientRect();
        thumb.style.transition = '';
      }
    };
    new MutationObserver(place).observe(seg, { subtree: true, attributes: true, attributeFilter: ['aria-pressed'] });
    new ResizeObserver(place).observe(seg);
    place();
  }
}

async function boot() {
  window.__sdReady = true;
  await loadWoodScans();
  const restored = restoreFromLink();
  // Rectangle-only themes have no Unique walls control — stay on 4 walls.
  if (!dom.wallCounts && !WALL_COUNT_OPTIONS.includes(state.wallCount)) {
    state.wallCount = 4;
    state.walls = [];
  }
  bindSegmentThumbs();
  bindWallCount();
  bindSliders();
  bindWoods();
  bindBenches();
  bindFloor();
  bindHeater();
  bindDoor();
  bindReview();
  bindStepper();
  if (restored) {
    const last = dom.steps.length - 1;
    showStep(last);
    requestAnimationFrame(() => focusStep(last, { scroll: true }));
  }

  if (!webglAvailable()) {
    showFallback();
    return;
  }

  for (const key of new Set(HEATERS.map((heater) => heater.model))) heaterModel(key);

  try {
    initScene();
  } catch (err) {
    console.error('[sauna-designer] WebGL init failed', err);
    showFallback();
    return;
  }

  dom.fallback.hidden = true;
  rebuild();
  requestAnimationFrame(tick);
  requestAnimationFrame(() => dom.loading.classList.add('is-done'));
  if (restored && new URLSearchParams(window.location.search).get('breakdown') === '1') openBreakdown({ push: false });

  window.WSDesigner = {
    state, CONFIG, HEATERS, WOODS, parseFtIn, computeSpec, checkFit, heaterOptions, roomVolumeCF, panelsAround,
    BENCH_LAYOUTS, benchNeeds, benchFits, benchChoices, benchRuns, benchSlats, benchFrame, benchPanels, benchFascia, cutPieces, buyBoards, materialPlan, STOCK, resolveLSide, duckboardBoards, doorPosition, orbitTo, heaterChoices, resolveHeater, heaterSpans, ventPlacement,
    buildPolyPlan, roomPlan, isPolyRoom, defaultWallLengths, wallLengthLabel,
    priceDesign: (s = state) => priceDesign(s, PRICES), encodeDesign, decodeDesign, designURL,
    debug: { camera, controls, heaterModels, get walls() { return walls; }, get ceiling() { return ceiling; }, get roomGroup() { return roomGroup; } },
  };
}

if (dom.section) boot();
